import { devWarn } from "../../utils/devWarning";

import { mergeAnnotationStudioLabels } from "./messages";
import { findCapturedFrameAt } from "./capturedFrameIdentity";
import { orderFrameIdsBySequenceAndTime } from "./frameOrdering";
import {
  hasAnnotationTargetContent,
  hasSameAnnotationTargetContent,
} from "./annotationTargetContent";
import {
  hydrateAnnotationStudioInitialSession,
  type HydratedAnnotationStudioSession,
} from "./initialSession";
import { createPersistedTrackingConditioningKeyframes } from "./trackingConditioning";
import type {
  AnnotationAuthoringDriver,
  AnnotationFrameDraft,
  AnnotationFrameSummary,
  AnnotationImageRenderResource,
  AnnotationIndexedAuthoringDriver,
  AnnotationMediaSource,
  AnnotationOperation,
  AnnotationOperationAvailability,
  AnnotationOperationError as AnnotationOperationErrorType,
  AnnotationPointKind,
  AnnotationRenderSourceRef,
  AnnotationSavedFrameIndexSnapshot,
  AnnotationSavedFrameMutationReceipt,
  AnnotationSegmentationDriver,
  AnnotationSequence,
  AnnotationSequenceDirectorySnapshot,
  AnnotationSourceRevision,
  AnnotationSetDraft,
  AnnotationStudioController,
  AnnotationStudioControllerOptions,
  AnnotationStudioControllerSnapshot,
  AnnotationStudioInitialSession,
  AnnotationStudioLabels,
  AnnotationStudioLeaveReason,
  AnnotationStudioLocale,
  AnnotationWorkspaceSnapshot,
  CapturedFrameRenderResource,
  DisposableRenderResource,
  FrameTargetAnnotationDraft,
  NormalizedAnnotationPoint,
  ReplayableRenderSourceDescriptor,
  SavedAnnotationSet,
  SegmentationResult,
  SegmentTargetsRequestTarget,
  SequenceDirectoryQuery,
  TrackingFrameRenderResource,
  TrackingPreviewFrameMetadata,
  TrackingPreviewStreamFrame,
  TrackingPreviewTargetMedia,
} from "./types";
import { AnnotationOperationError } from "./types";

const idleOperation = () => ({ pending: false, error: null });
const materializedFrameCacheLimit = 24;
let didWarnAboutLegacyTrackingPreview = false;

function initialOperations(): AnnotationStudioControllerSnapshot["operations"] {
  return {
    capture: idleOperation(),
    segment: idleOperation(),
    track: idleOperation(),
    save: idleOperation(),
    remove: idleOperation(),
    render: idleOperation(),
  };
}

function resolveSequence(
  workspace: AnnotationWorkspaceSnapshot,
  sequenceId: string | null,
  directory: AnnotationSequenceDirectorySnapshot | null = null,
  getCachedSequence?: (sequenceId: string) => AnnotationSequence | null,
): AnnotationSequence | null {
  if (sequenceId === null) return null;
  return (
    workspace.sequences.find((candidate) => candidate.id === sequenceId) ??
    directory?.items.find((candidate) => candidate.id === sequenceId) ??
    getCachedSequence?.(sequenceId) ??
    null
  );
}

function resolveStreamId(
  workspace: AnnotationWorkspaceSnapshot,
  sequence: AnnotationSequence | null,
  preferredStreamId: string | null = null,
): string | null {
  if (sequence === null) return null;
  if (
    preferredStreamId !== null &&
    sequence?.streamIds.includes(preferredStreamId) &&
    workspace.streams.some((stream) => stream.id === preferredStreamId)
  ) {
    return preferredStreamId;
  }
  return (
    sequence?.streamIds.find((id) => workspace.streams.some((stream) => stream.id === id)) ?? null
  );
}

function sameSequenceDirectoryQuery(
  left: SequenceDirectoryQuery,
  right: SequenceDirectoryQuery,
): boolean {
  if (
    left.streamId !== right.streamId ||
    (left.contentScope ?? "all") !== (right.contentScope ?? "all")
  ) {
    return false;
  }
  const leftAttentionIds = left.attentionSequenceIds;
  const rightAttentionIds = right.attentionSequenceIds;
  if (leftAttentionIds === rightAttentionIds) return true;
  if (
    leftAttentionIds === undefined ||
    rightAttentionIds === undefined ||
    leftAttentionIds.length !== rightAttentionIds.length
  ) {
    return false;
  }
  return leftAttentionIds.every((sequenceId, index) => sequenceId === rightAttentionIds[index]);
}

function createInitialSnapshot(
  options: AnnotationStudioControllerOptions,
  workspace: AnnotationWorkspaceSnapshot,
  hydratedSession: HydratedAnnotationStudioSession | null,
  sequenceDirectory: AnnotationSequenceDirectorySnapshot | null,
  savedFrameIndex: AnnotationSavedFrameIndexSnapshot | null,
): AnnotationStudioControllerSnapshot {
  const selectedSequenceId = hydratedSession
    ? hydratedSession.selectedSequenceId
    : (sequenceDirectory?.items[0]?.id ?? workspace.sequences[0]?.id ?? null);
  const selectedSequence = resolveSequence(
    workspace,
    selectedSequenceId,
    sequenceDirectory,
    options.sequenceDirectory?.getCachedSequence.bind(options.sequenceDirectory),
  );
  const selectedStreamId = hydratedSession
    ? hydratedSession.selectedStreamId
    : resolveStreamId(workspace, selectedSequence);
  const selectedTargetId = hydratedSession
    ? hydratedSession.selectedTargetId
    : (workspace.targets[0]?.id ?? null);
  const draft = hydratedSession?.draft ?? null;
  const validationContext = {
    workspace,
    sequenceId: selectedSequenceId,
    streamId: selectedStreamId,
    targetId: selectedTargetId,
    draft,
  };
  const authoringAvailability =
    options.mode === "authoring" ? options.authoring.getAvailability(validationContext) : undefined;
  return {
    connected: false,
    mode: options.mode,
    exitPreflightOrder: options.exitPreflightOrder ?? "validate-then-save",
    locale: options.locale,
    labels: mergeAnnotationStudioLabels(options.labels),
    workspace,
    sequenceDirectory,
    pinnedSequence:
      selectedSequence && !sequenceDirectory?.items.some((item) => item.id === selectedSequence.id)
        ? selectedSequence
        : null,
    savedFrameIndex,
    selectedSequenceId,
    selectedStreamId,
    selectedTargetId,
    viewMode: hydratedSession?.viewMode ?? "browse",
    draft,
    isDirty: false,
    saveConflictFrameIds: [],
    canUndo: false,
    canRedo: false,
    leaveConfirmation: null,
    authoringAvailability,
    segmentationAvailability:
      options.segmentation?.getAvailability(validationContext) ?? authoringAvailability,
    trackingAvailable: options.trackingPreview !== undefined,
    trackingUsesCanonicalConditioning: options.trackingPreview?.conditioningSource === "canonical",
    trackingAvailability: options.trackingPreview?.getAvailability(validationContext),
    trackingPreview: {
      status: "idle",
      targetMedia: null,
      framesSeen: 0,
      latestFrame: null,
      latestFrameSourceRef: null,
      error: null,
    },
    operations: initialOperations(),
    annotationSetValidation: options.annotationSetValidation
      ? {
          status: "idle",
          availability: options.annotationSetValidation.getAvailability(validationContext),
          issues: [],
          error: null,
        }
      : null,
  };
}

function resolveSegmentationDriver(
  options: AnnotationStudioControllerOptions,
): AnnotationSegmentationDriver | undefined {
  return options.segmentation ?? (options.mode === "authoring" ? options.authoring : undefined);
}

function clampCoordinate(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function sourceRef(descriptor: ReplayableRenderSourceDescriptor) {
  return { id: descriptor.id, revision: descriptor.revision } as const;
}

function descriptorRegistryKey(ref: AnnotationRenderSourceRef): string {
  return `${ref.id}:${ref.revision}`;
}

function initialPointSerial(draft: AnnotationSetDraft | null): number {
  let serial = 0;
  for (const frame of Object.values(draft?.frames ?? {})) {
    for (const target of Object.values(frame.targets)) {
      for (const point of target.points) {
        const match = /^point-(\d+)$/.exec(point.id);
        if (match) serial = Math.max(serial, Number(match[1]));
      }
    }
  }
  return serial;
}

function initialDraftSerial(draft: AnnotationSetDraft | null): number {
  const match = draft ? /^draft-(\d+)$/.exec(draft.draftId) : null;
  return match ? Number(match[1]) : 0;
}

function hasBlockingDirtyTrackingTargets(
  draft: AnnotationSetDraft,
  streamId: string,
  conflictFrameIds: ReadonlySet<string>,
): boolean {
  return draft.dirtyScopes.some(
    (scope) =>
      scope.kind === "target" &&
      draft.frames[scope.frameId]?.streamId === streamId &&
      !conflictFrameIds.has(scope.frameId),
  );
}

function trackingConflictFrameIds(
  snapshot: AnnotationStudioControllerSnapshot,
): ReadonlySet<string> {
  const frameIds = new Set(snapshot.saveConflictFrameIds ?? []);
  for (const issue of snapshot.annotationSetValidation?.issues ?? []) {
    if (issue.frameId) frameIds.add(issue.frameId);
    for (const frameId of issue.affectedFrameIds ?? []) frameIds.add(frameId);
    if (issue.location) frameIds.add(issue.location.frameId);
    for (const location of issue.affectedLocations ?? []) frameIds.add(location.frameId);
  }
  return frameIds;
}

function trackingPreviewMetadata(frame: TrackingPreviewStreamFrame): TrackingPreviewFrameMetadata {
  return {
    frameIndex: frame.frameIndex,
    timestampSeconds: frame.timestampSeconds,
    ...(frame.targets !== undefined ? { targets: frame.targets } : {}),
    ...(frame.frameSource !== undefined ? { frameSourceRef: sourceRef(frame.frameSource) } : {}),
  };
}

function normalizeOperationError(
  operation: AnnotationOperation,
  error: unknown,
): AnnotationOperationErrorType {
  if (error instanceof AnnotationOperationError) return error;
  return new AnnotationOperationError(operation, "unknown");
}

function isImageRenderResource(
  resource: DisposableRenderResource,
): resource is AnnotationImageRenderResource {
  if (!("image" in resource)) return false;
  const image = (resource as Partial<AnnotationImageRenderResource>).image;
  return typeof image === "string" || (typeof image === "object" && image !== null);
}

function isAborted(signal: AbortSignal): boolean {
  return signal.aborted;
}

function sameFrameLocator(
  left: Pick<AnnotationFrameSummary, "frameId" | "sequenceId" | "streamId" | "timestampSeconds">,
  right: Pick<AnnotationFrameDraft, "frameId" | "sequenceId" | "streamId" | "timestampSeconds">,
): boolean {
  return (
    left.frameId === right.frameId &&
    left.sequenceId === right.sequenceId &&
    left.streamId === right.streamId &&
    left.timestampSeconds === right.timestampSeconds
  );
}

function isSavedFrameMutationReceipt(
  entity: SavedAnnotationSet | AnnotationSavedFrameMutationReceipt,
): entity is AnnotationSavedFrameMutationReceipt {
  return "savedFrameIndexRevision" in entity;
}

type AnyAnnotationAuthoringDriver = AnnotationAuthoringDriver | AnnotationIndexedAuthoringDriver;

type ResourceSlot = "frame" | "mask";

type ActiveResource = {
  readonly descriptorKey: string;
  readonly resource: DisposableRenderResource;
};

type PendingResource = {
  readonly descriptorKey: string;
  readonly token: symbol;
  readonly promise: Promise<void>;
};

type ActiveTrackingFrameResource = {
  readonly descriptorKey: string;
  readonly resource: TrackingFrameRenderResource;
};

type PendingTrackingFrameResource = {
  readonly descriptorKey: string;
  readonly token: symbol;
  readonly controller: AbortController;
  readonly detachSessionAbort: () => void;
};

type PendingLeaveRequest = {
  readonly decision: Promise<boolean>;
  readonly resolve: (allowed: boolean) => void;
};

type TargetHistoryChange = Readonly<{
  targetId: string;
  before: FrameTargetAnnotationDraft | undefined;
  after: FrameTargetAnnotationDraft | undefined;
}>;

type AnnotationHistoryEntry = Readonly<{
  frameId: string;
  changes: readonly TargetHistoryChange[];
  beforeDirtyTargetIds: readonly string[];
  afterDirtyTargetIds: readonly string[];
}>;

const annotationHistoryLimit = 100;

type AnnotationSetValidationInputIdentity = Readonly<{
  collectionId: string;
  workspaceRevision: number;
  draftId: string | null;
  baseSetId: string | null;
  baseRevision: number | null;
  orderedFrameIds: AnnotationSetDraft["orderedFrameIds"] | null;
  frames: AnnotationSetDraft["frames"] | null;
  canonicalDirtyTargets: readonly Readonly<{
    frameId: string;
    targetId: string;
    target: FrameTargetAnnotationDraft | undefined;
  }>[];
}>;

function annotationSetValidationInputIdentity(
  snapshot: Pick<AnnotationStudioControllerSnapshot, "workspace" | "draft">,
  canonical = false,
): AnnotationSetValidationInputIdentity {
  const { draft, workspace } = snapshot;
  const canonicalDirtyTargets = canonical
    ? (draft?.dirtyScopes.flatMap((scope) =>
        scope.kind === "target"
          ? [
              {
                frameId: scope.frameId,
                targetId: scope.targetId,
                target: draft.frames[scope.frameId]?.targets[scope.targetId],
              },
            ]
          : [],
      ) ?? [])
    : [];
  return {
    collectionId: workspace.collectionId,
    workspaceRevision: workspace.revision,
    draftId: canonical ? null : (draft?.draftId ?? null),
    baseSetId: canonical ? null : (draft?.base?.setId ?? null),
    baseRevision: canonical ? null : (draft?.base?.revision ?? null),
    orderedFrameIds: canonical ? null : (draft?.orderedFrameIds ?? null),
    frames: canonical ? null : (draft?.frames ?? null),
    canonicalDirtyTargets,
  };
}

function annotationSetValidationInputsMatch(
  left: AnnotationSetValidationInputIdentity,
  right: AnnotationSetValidationInputIdentity,
): boolean {
  return (
    left.collectionId === right.collectionId &&
    left.workspaceRevision === right.workspaceRevision &&
    left.draftId === right.draftId &&
    left.baseSetId === right.baseSetId &&
    left.baseRevision === right.baseRevision &&
    left.orderedFrameIds === right.orderedFrameIds &&
    left.frames === right.frames &&
    left.canonicalDirtyTargets.length === right.canonicalDirtyTargets.length &&
    left.canonicalDirtyTargets.every((target, index) => {
      const other = right.canonicalDirtyTargets[index];
      return (
        other !== undefined &&
        target.frameId === other.frameId &&
        target.targetId === other.targetId &&
        target.target === other.target
      );
    })
  );
}

export class DefaultAnnotationStudioController implements AnnotationStudioController {
  private snapshot: AnnotationStudioControllerSnapshot;
  private readonly listeners = new Set<() => void>();
  private readonly capturedFrameResourceListeners = new Set<() => void>();
  private readonly trackingFrameResourceListeners = new Set<() => void>();
  private readonly descriptors = new Map<string, ReplayableRenderSourceDescriptor>();
  private readonly activeResources = new Map<ResourceSlot, ActiveResource>();
  private readonly pendingResources = new Map<ResourceSlot, PendingResource>();
  private readonly operationControllers = new Map<AnnotationOperation, AbortController>();
  private readonly operationSerial = new Map<AnnotationOperation, number>();
  private annotationSetValidationController: AbortController | null = null;
  private annotationSetValidationSerial = 0;
  private activeTrackingIterator: AsyncIterator<TrackingPreviewStreamFrame> | null = null;
  private trackingPausePromise: Promise<void> | null = null;
  private resumeTrackingProducer: (() => void) | null = null;
  private latestTrackingFrameDescriptor: Readonly<{
    descriptorKey: string;
    descriptor: ReplayableRenderSourceDescriptor<TrackingFrameRenderResource>;
  }> | null = null;
  private trackingSessionAbort: AbortController | null = null;
  private trackingFrameResource: ActiveTrackingFrameResource | null = null;
  private pendingTrackingFrameResource: PendingTrackingFrameResource | null = null;
  private sourceUnsubscribe: (() => void) | null = null;
  private directoryUnsubscribe: (() => void) | null = null;
  private savedFrameIndexUnsubscribe: (() => void) | null = null;
  private ensureSequenceController: AbortController | null = null;
  private ensureSequenceSerial = 0;
  private materializeFrameController: AbortController | null = null;
  private materializeFrameSerial = 0;
  private readonly materializedFrameRevisions = new Map<string, AnnotationSourceRevision>();
  private readonly materializedFrameAccess = new Map<string, number>();
  private materializedFrameAccessSerial = 0;
  private retainedMaterializedFrameIds = new Set<string>();
  private connectionAbort: AbortController | null = null;
  private connections = 0;
  private pointSerial = 0;
  private draftSerial = 0;
  private readonly undoStack: AnnotationHistoryEntry[] = [];
  private readonly redoStack: AnnotationHistoryEntry[] = [];
  private savedDraftBaseline: AnnotationSetDraft | null = null;
  private pendingLeaveRequest: PendingLeaveRequest | null = null;
  private disposed = false;
  private availabilityCache: {
    workspace: AnnotationWorkspaceSnapshot;
    sequenceId: string | null;
    streamId: string | null;
    targetId: string | null;
    draft: AnnotationSetDraft | null;
    authoring?: AnnotationOperationAvailability;
    segmentation?: AnnotationOperationAvailability;
    tracking?: AnnotationOperationAvailability;
    validation?: AnnotationOperationAvailability;
  } | null = null;

  constructor(private readonly options: AnnotationStudioControllerOptions) {
    if (options.trackingPreview && !didWarnAboutLegacyTrackingPreview) {
      didWarnAboutLegacyTrackingPreview = true;
      devWarn(
        "[c6] AnnotationStudioControllerOptions.trackingPreview is deprecated. Run tracking in the product and pass MediaAnnotationStudio an AnnotationMediaResultBinding for display.",
      );
    }
    const asyncCollectionCapabilities = [
      options.sequenceDirectory,
      options.savedFrameIndex,
      options.frameMaterializer,
    ].filter(Boolean).length;
    if (asyncCollectionCapabilities !== 0 && asyncCollectionCapabilities !== 3) {
      throw new Error(
        "Invalid AnnotationStudioControllerOptions: sequenceDirectory, savedFrameIndex, and frameMaterializer must be provided together.",
      );
    }
    if (
      options.annotationSetValidation?.validationSource === "canonical" &&
      !options.annotationSetValidation.getSourceRevision
    ) {
      throw new Error(
        "Invalid AnnotationStudioControllerOptions: canonical validation requires getSourceRevision().",
      );
    }
    if (
      options.exitPreflightOrder === "save-then-validate" &&
      options.annotationSetValidation?.validationSource !== "canonical"
    ) {
      throw new Error(
        "Invalid AnnotationStudioControllerOptions: save-then-validate requires canonical validation.",
      );
    }
    if (
      options.trackingPreview?.conditioningSource === "canonical" &&
      !options.trackingPreview.getConditioningRevision
    ) {
      throw new Error(
        "Invalid AnnotationStudioControllerOptions: canonical tracking requires getConditioningRevision().",
      );
    }
    const workspace = options.workspaceSource.getSnapshot();
    const sequenceDirectory = options.sequenceDirectory?.getSnapshot() ?? null;
    const savedFrameIndex = options.savedFrameIndex?.getSnapshot() ?? null;
    const hydrationWorkspace = options.initialSession
      ? this.createHydrationWorkspace(workspace, options.initialSession, sequenceDirectory)
      : workspace;
    const hydratedSession = options.initialSession
      ? hydrateAnnotationStudioInitialSession(hydrationWorkspace, options.initialSession)
      : null;
    this.snapshot = createInitialSnapshot(
      options,
      workspace,
      hydratedSession,
      sequenceDirectory,
      savedFrameIndex,
    );
    this.savedDraftBaseline = hydratedSession?.draft ?? null;
    for (const summary of savedFrameIndex?.orderedFrames ?? []) {
      const hydratedFrame = hydratedSession?.draft.frames[summary.frameId];
      if (hydratedFrame && sameFrameLocator(summary, hydratedFrame)) {
        this.materializedFrameRevisions.set(summary.frameId, summary.revision);
        this.touchMaterializedFrame(summary.frameId);
      }
    }
    this.pointSerial = initialPointSerial(hydratedSession?.draft ?? null);
    this.draftSerial = initialDraftSerial(hydratedSession?.draft ?? null);
    for (const descriptor of hydratedSession?.descriptors ?? []) {
      this.descriptors.set(descriptorRegistryKey(descriptor), descriptor);
    }
    this.evictMaterializedFrames();
  }

  getSnapshot = (): AnnotationStudioControllerSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    let subscribed = true;
    return () => {
      if (!subscribed) return;
      subscribed = false;
      this.listeners.delete(listener);
    };
  };

  getCapturedFrameRenderResource = (
    ref: AnnotationRenderSourceRef,
  ): CapturedFrameRenderResource | null => {
    const descriptorKey = descriptorRegistryKey(ref);
    const activeFrame = this.activeResources.get("frame");
    if (activeFrame?.descriptorKey !== descriptorKey) return null;
    return isImageRenderResource(activeFrame.resource) ? activeFrame.resource : null;
  };

  subscribeCapturedFrameRenderResource = (listener: () => void): (() => void) => {
    this.capturedFrameResourceListeners.add(listener);
    let subscribed = true;
    return () => {
      if (!subscribed) return;
      subscribed = false;
      this.capturedFrameResourceListeners.delete(listener);
    };
  };

  resolveMediaSource = (sequenceId: string, streamId: string): AnnotationMediaSource | null => {
    const workspace = this.snapshot.workspace;
    const sequence = this.findSequence(sequenceId);
    const stream = workspace.streams.find((candidate) => candidate.id === streamId);
    if (!sequence?.streamIds.includes(streamId) || !stream) return null;
    const resolved = this.options.resolveMediaSource?.({ workspace, sequence, stream });
    return resolved === undefined ? (stream.media ?? null) : resolved;
  };

  getTrackingFrameRenderResource = (
    ref: AnnotationRenderSourceRef,
  ): TrackingFrameRenderResource | null => {
    const descriptorKey = `${ref.id}:${ref.revision}`;
    const current = this.trackingFrameResource;
    if (current?.descriptorKey === descriptorKey) return current.resource;
    // 요청 프레임을 아직 획득 중이면 직전 프레임을 유지한다(stale-while-pending) — null로
    // 떨어지면 viewport가 프레임 사이마다 비디오 fallback으로 튀어 재생이 깜빡인다.
    if (this.pendingTrackingFrameResource?.descriptorKey === descriptorKey && current) {
      return current.resource;
    }
    return null;
  };

  prepareTrackingFrameRenderResource = (ref: AnnotationRenderSourceRef | null): void => {
    if (this.disposed) return;
    if (ref === null) {
      this.releaseTrackingFrameResource();
      return;
    }
    const descriptorKey = `${ref.id}:${ref.revision}`;
    const latestDescriptor = this.latestTrackingFrameDescriptor;
    const sessionSignal = this.trackingSessionAbort?.signal;
    // A render committed for an older producer frame can reach this method after the stream has
    // already advanced. Ignore that stale selection instead of releasing the current swap.
    if (
      latestDescriptor?.descriptorKey !== descriptorKey ||
      !sessionSignal ||
      sessionSignal.aborted
    )
      return;
    this.setTrackingFrameSource(latestDescriptor.descriptor, sessionSignal);
  };

  subscribeTrackingFrameRenderResource = (listener: () => void): (() => void) => {
    this.trackingFrameResourceListeners.add(listener);
    let subscribed = true;
    return () => {
      if (!subscribed) return;
      subscribed = false;
      this.trackingFrameResourceListeners.delete(listener);
    };
  };

  connect = (): (() => void) => {
    this.assertNotDisposed();
    this.connections += 1;
    if (this.connections === 1) {
      this.connectionAbort = new AbortController();
      this.sourceUnsubscribe = this.options.workspaceSource.subscribe(() => {
        this.reconcileWorkspace(this.options.workspaceSource.getSnapshot());
      });
      this.directoryUnsubscribe =
        this.options.sequenceDirectory?.subscribe(() => {
          this.reconcileSequenceDirectory(this.options.sequenceDirectory?.getSnapshot() ?? null);
        }) ?? null;
      this.savedFrameIndexUnsubscribe =
        this.options.savedFrameIndex?.subscribe(() => {
          this.reconcileSavedFrameIndex(this.options.savedFrameIndex?.getSnapshot() ?? null);
        }) ?? null;
      this.reconcileWorkspace(this.options.workspaceSource.getSnapshot());
      this.reconcileSequenceDirectory(this.options.sequenceDirectory?.getSnapshot() ?? null);
      this.reconcileSavedFrameIndex(this.options.savedFrameIndex?.getSnapshot() ?? null);
      this.patchSnapshot({ connected: true });
      void this.restoreActiveResources();
    }

    let connected = true;
    return () => {
      if (!connected) return;
      connected = false;
      this.connections = Math.max(0, this.connections - 1);
      if (this.connections === 0) this.disconnect();
    };
  };

  dispose = (): void => {
    if (this.disposed) return;
    this.settlePendingLeave(false);
    this.disposed = true;
    this.connections = 0;
    this.disconnect();
    this.descriptors.clear();
    this.materializedFrameRevisions.clear();
    this.materializedFrameAccess.clear();
    this.retainedMaterializedFrameIds.clear();
    this.listeners.clear();
    this.capturedFrameResourceListeners.clear();
    this.trackingFrameResourceListeners.clear();
    this.clearHistory();
    this.savedDraftBaseline = null;
    this.snapshot = {
      ...this.snapshot,
      connected: false,
      draft: null,
      isDirty: false,
      canUndo: false,
      canRedo: false,
      leaveConfirmation: null,
      viewMode: "browse",
    };
  };

  requestLeave = (reason: AnnotationStudioLeaveReason): Promise<boolean> => {
    this.assertNotDisposed();
    if (!this.snapshot.isDirty) return Promise.resolve(true);
    if (this.pendingLeaveRequest) return this.pendingLeaveRequest.decision;

    let resolveDecision!: (allowed: boolean) => void;
    const decision = new Promise<boolean>((resolve) => {
      resolveDecision = resolve;
    });
    this.pendingLeaveRequest = { decision, resolve: resolveDecision };
    this.patchSnapshot({ leaveConfirmation: { reason } });
    return decision;
  };

  confirmLeave = (): void => {
    this.assertNotDisposed();
    this.settlePendingLeave(true);
  };

  cancelLeave = (): void => {
    this.assertNotDisposed();
    this.settlePendingLeave(false);
  };

  updatePresentation = (
    locale: AnnotationStudioLocale,
    labels?: Partial<AnnotationStudioLabels>,
  ): void => {
    this.assertNotDisposed();
    const nextLabels = mergeAnnotationStudioLabels(labels);
    if (this.snapshot.locale === locale && this.snapshot.labels === nextLabels) return;
    this.patchSnapshot({ locale, labels: nextLabels });
  };

  selectSequence = (sequenceId: string): void => {
    this.assertNotDisposed();
    const sequence = this.findSequence(sequenceId);
    if (!sequence) return;
    if (sequenceId === this.snapshot.selectedSequenceId) return;
    this.resetSegmentationOnContextChange();
    this.stopTrackingPreview();
    const draft = this.snapshot.draft;
    this.patchSnapshot({
      selectedSequenceId: sequenceId,
      selectedStreamId: resolveStreamId(
        this.snapshot.workspace,
        sequence,
        this.snapshot.selectedStreamId,
      ),
      pinnedSequence: this.resolvePinnedSequenceAfterIntent(sequence),
      draft: draft && draft.activeFrameId !== null ? { ...draft, activeFrameId: null } : draft,
    });
    void this.restoreActiveResources();
  };

  selectStream = (streamId: string): void => {
    this.assertNotDisposed();
    const sequence = this.findSequence(this.snapshot.selectedSequenceId);
    if (!this.snapshot.workspace.streams.some((stream) => stream.id === streamId)) return;
    if (!sequence?.streamIds.includes(streamId) && this.options.sequenceDirectory) {
      this.resetSegmentationOnContextChange();
      this.stopTrackingPreview();
      const draft = this.snapshot.draft;
      this.patchSnapshot({
        selectedSequenceId: null,
        selectedStreamId: streamId,
        pinnedSequence: null,
        draft: draft && draft.activeFrameId !== null ? { ...draft, activeFrameId: null } : draft,
      });
      const currentQuery = this.options.sequenceDirectory.getSnapshot().query;
      this.options.sequenceDirectory.setQuery({ ...currentQuery, streamId });
      return;
    }
    if (!sequence?.streamIds.includes(streamId)) return;
    if (streamId === this.snapshot.selectedStreamId) {
      const directory = this.options.sequenceDirectory;
      if (directory && directory.getSnapshot().query.streamId !== streamId) {
        directory.setQuery({ ...directory.getSnapshot().query, streamId });
      }
      return;
    }
    this.resetSegmentationOnContextChange();
    this.stopTrackingPreview();
    const draft = this.snapshot.draft;
    this.patchSnapshot({
      selectedStreamId: streamId,
      draft: draft && draft.activeFrameId !== null ? { ...draft, activeFrameId: null } : draft,
    });
    const directory = this.options.sequenceDirectory;
    if (directory && directory.getSnapshot().query.streamId !== streamId) {
      directory.setQuery({ ...directory.getSnapshot().query, streamId });
    }
    void this.restoreActiveResources();
  };

  setSequenceDirectoryQuery = (query: SequenceDirectoryQuery): void => {
    this.assertNotDisposed();
    this.options.sequenceDirectory?.setQuery(query);
  };

  setRetainedMaterializedFrameIds = (frameIds: readonly string[]): void => {
    this.assertNotDisposed();
    this.retainedMaterializedFrameIds = new Set(frameIds);
    this.evictMaterializedFrames();
  };

  loadNextSequencePage = (): void => {
    this.assertNotDisposed();
    this.options.sequenceDirectory?.loadNextPage();
  };

  retrySequenceDirectory = (): void => {
    this.assertNotDisposed();
    this.options.sequenceDirectory?.retry();
  };

  ensureSequence = async (sequenceId: string): Promise<boolean> => {
    this.assertNotDisposed();
    const source = this.options.sequenceDirectory;
    if (!source) return this.findSequence(sequenceId) !== null;
    this.ensureSequenceController?.abort();
    this.ensureSequenceController = null;
    this.ensureSequenceSerial += 1;
    const cached = this.findSequence(sequenceId);
    if (cached) {
      this.patchSnapshot({ pinnedSequence: this.resolvePinnedSequenceAfterIntent(cached) });
      return true;
    }

    const controller = new AbortController();
    const serial = this.ensureSequenceSerial;
    this.ensureSequenceController = controller;
    try {
      const sequence = await source.ensureSequence(sequenceId, { signal: controller.signal });
      if (controller.signal.aborted || serial !== this.ensureSequenceSerial) return false;
      this.patchSnapshot({ pinnedSequence: this.resolvePinnedSequenceAfterIntent(sequence) });
      return true;
    } catch {
      return false;
    } finally {
      if (serial === this.ensureSequenceSerial) this.ensureSequenceController = null;
    }
  };

  materializeAnnotationFrame = async (frameId: string): Promise<boolean> => {
    this.assertNotDisposed();
    const indexSource = this.options.savedFrameIndex;
    const materializer = this.options.frameMaterializer;
    if (!indexSource || !materializer) return this.snapshot.draft?.frames[frameId] !== undefined;

    this.materializeFrameController?.abort();
    const controller = new AbortController();
    const serial = ++this.materializeFrameSerial;
    this.materializeFrameController = controller;
    try {
      const startIndex = indexSource.getSnapshot();
      const summary = this.findSavedFrameSummary(frameId, startIndex);
      if (!summary) return false;
      const existing = this.snapshot.draft?.frames[frameId];
      if (existing && Object.is(this.materializedFrameRevisions.get(frameId), summary.revision)) {
        this.touchMaterializedFrame(frameId);
        return true;
      }
      if (existing && this.frameHasDirtyTargets(frameId)) return false;
      if (!(await this.ensureSequence(summary.sequenceId))) return false;
      if (controller.signal.aborted || serial !== this.materializeFrameSerial) return false;

      const result = await materializer.materializeFrame(
        { frame: summary, expectedRevision: summary.revision },
        { signal: controller.signal },
      );
      if (controller.signal.aborted || serial !== this.materializeFrameSerial) return false;
      const currentIndex = indexSource.getSnapshot();
      const currentSummary = this.findSavedFrameSummary(summary.frameId, currentIndex);
      if (
        !currentSummary ||
        !Object.is(currentSummary.revision, summary.revision) ||
        !Object.is(result.revision, summary.revision) ||
        !sameFrameLocator(currentSummary, result.frame)
      ) {
        return false;
      }

      const sequence = this.findSequence(summary.sequenceId);
      if (!sequence) return false;
      const hydrationWorkspace = this.workspaceWithSequence(sequence, currentIndex.set);
      const currentDraft = this.snapshot.draft;
      const draftId = currentDraft?.draftId ?? `draft-${++this.draftSerial}`;
      const hydrated = hydrateAnnotationStudioInitialSession(hydrationWorkspace, {
        collectionId: hydrationWorkspace.collectionId,
        workspaceRevision: hydrationWorkspace.revision,
        draftId,
        orderedFrames: [result.frame],
        activeFrameId: null,
        selectedSequenceId: summary.sequenceId,
        selectedStreamId: summary.streamId,
        selectedTargetId: this.snapshot.selectedTargetId,
        viewMode: "browse",
      });
      const materializedFrame = hydrated.draft.frames[frameId];
      if (!materializedFrame) return false;
      const latestDraft = this.snapshot.draft;
      if (latestDraft && latestDraft.draftId !== draftId) return false;
      if (latestDraft?.frames[frameId] && this.frameHasDirtyTargets(frameId)) return false;
      for (const descriptor of hydrated.descriptors) {
        this.descriptors.set(descriptorRegistryKey(descriptor), descriptor);
      }
      const frames = { ...(latestDraft?.frames ?? {}), [frameId]: materializedFrame };
      const persistedOrder = currentIndex.orderedFrames.flatMap((frame) =>
        frames[frame.frameId] ? [frame.frameId] : [],
      );
      const persistedIds = new Set(currentIndex.orderedFrames.map((frame) => frame.frameId));
      const localOrder = (latestDraft?.orderedFrameIds ?? []).filter(
        (candidate) => !persistedIds.has(candidate) && frames[candidate] !== undefined,
      );
      const draft: AnnotationSetDraft = latestDraft
        ? {
            ...latestDraft,
            base: currentIndex.set ?? latestDraft.base,
            orderedFrameIds: [...persistedOrder, ...localOrder],
            frames,
          }
        : {
            draftId,
            base: currentIndex.set ?? undefined,
            orderedFrameIds: [...persistedOrder, ...localOrder],
            frames,
            activeFrameId: null,
            dirtyScopes: [],
          };
      this.materializedFrameRevisions.set(frameId, summary.revision);
      this.touchMaterializedFrame(frameId);
      this.rememberMaterializedFrame(draft, frameId, materializedFrame);
      this.patchSnapshot({ draft });
      this.evictMaterializedFrames(frameId);
      return true;
    } catch {
      return false;
    } finally {
      if (serial === this.materializeFrameSerial) this.materializeFrameController = null;
    }
  };

  selectTarget = (targetId: string): void => {
    this.assertNotDisposed();
    if (!this.snapshot.workspace.targets.some((target) => target.id === targetId)) return;
    this.patchSnapshot({ selectedTargetId: targetId });
    void this.restoreActiveResources();
  };

  captureFrame = async (timestampSeconds = 0): Promise<void> => {
    this.assertAuthoring("capture");
    const { selectedSequenceId, selectedStreamId } = this.snapshot;
    if (selectedSequenceId === null || selectedStreamId === null) return;
    if (
      findCapturedFrameAt(
        this.snapshot.draft,
        selectedSequenceId,
        selectedStreamId,
        timestampSeconds,
      ) ||
      this.snapshot.savedFrameIndex?.orderedFrames.some(
        (frame) =>
          frame.sequenceId === selectedSequenceId &&
          frame.streamId === selectedStreamId &&
          Math.abs(frame.timestampSeconds - timestampSeconds) <= 1e-3,
      )
    ) {
      return;
    }
    const authoring = this.options.authoring as AnyAnnotationAuthoringDriver;
    if (!this.getAuthoringAvailability().allowed) return;

    const operation = this.startOperation("capture");
    try {
      const captured = await authoring.captureFrame(
        { sequenceId: selectedSequenceId, streamId: selectedStreamId, timestampSeconds },
        { signal: operation.signal },
      );
      if (!this.isCurrentOperation("capture", operation.serial) || isAborted(operation.signal)) {
        return;
      }
      if (
        this.snapshot.selectedSequenceId !== selectedSequenceId ||
        this.snapshot.selectedStreamId !== selectedStreamId
      ) {
        this.finishOperation("capture", operation.serial);
        return;
      }
      if (
        findCapturedFrameAt(
          this.snapshot.draft,
          captured.sequenceId,
          captured.streamId,
          captured.timestampSeconds,
        ) ||
        this.snapshot.savedFrameIndex?.orderedFrames.some(
          (frame) =>
            frame.sequenceId === captured.sequenceId &&
            frame.streamId === captured.streamId &&
            Math.abs(frame.timestampSeconds - captured.timestampSeconds) <= 1e-3,
        )
      ) {
        this.finishOperation("capture", operation.serial);
        return;
      }

      this.descriptors.set(descriptorRegistryKey(captured.frameSource), captured.frameSource);
      const frame: AnnotationFrameDraft = {
        frameId: captured.frameId,
        sequenceId: captured.sequenceId,
        streamId: captured.streamId,
        timestampSeconds: captured.timestampSeconds,
        sourceRevision: captured.sourceRevision,
        width: captured.width,
        height: captured.height,
        frameSource: sourceRef(captured.frameSource),
        targets: {},
      };
      const current = this.snapshot.draft;
      const frameDirtyScope = { kind: "frame", frameId: frame.frameId } as const;
      const frames = { ...(current?.frames ?? {}), [frame.frameId]: frame };
      const nextFrameIds = current?.orderedFrameIds.includes(frame.frameId)
        ? current.orderedFrameIds
        : [...(current?.orderedFrameIds ?? []), frame.frameId];
      const orderedFrameIds = orderFrameIdsBySequenceAndTime(
        nextFrameIds,
        frames,
        this.snapshot.workspace.sequences.map((sequence) => sequence.id),
      );
      const draft: AnnotationSetDraft = current
        ? {
            ...current,
            orderedFrameIds,
            frames,
            activeFrameId: frame.frameId,
            dirtyScopes: [
              ...current.dirtyScopes.filter(
                (scope) => scope.kind !== "frame" || scope.frameId !== frame.frameId,
              ),
              frameDirtyScope,
            ],
          }
        : {
            draftId: `draft-${++this.draftSerial}`,
            base: this.snapshot.savedFrameIndex?.set ?? undefined,
            orderedFrameIds,
            frames,
            activeFrameId: frame.frameId,
            dirtyScopes: [frameDirtyScope],
          };
      this.patchSnapshot({ draft, isDirty: true, viewMode: "browse" });
      this.finishOperation("capture", operation.serial);
      await this.restoreActiveResources();
    } catch (error) {
      this.failOperation("capture", operation, error);
    }
  };

  removeCapturedFrame = async (requestedFrameId?: string): Promise<void> => {
    this.assertAuthoring("remove");
    let draft = this.snapshot.draft;
    const frameId = requestedFrameId ?? draft?.activeFrameId;
    if (!draft || frameId === null || frameId === undefined) return;
    if (!draft.orderedFrameIds.includes(frameId)) return;
    if (
      this.snapshot.operations.capture.pending ||
      this.snapshot.operations.segment.pending ||
      this.snapshot.operations.save.pending ||
      this.snapshot.operations.remove.pending
    ) {
      return;
    }

    const base = draft.base;
    const savedFrameExists =
      (base !== undefined &&
        this.snapshot.workspace.annotationSets
          .find((set) => set.setId === base.setId)
          ?.orderedFrames.some((frame) => frame.frameId === frameId)) ||
      this.findSavedFrameSummary(frameId) !== null;
    let removalOperation: { serial: number; signal: AbortSignal } | null = null;
    if (base && savedFrameExists) {
      if (!this.getAuthoringAvailability().allowed) return;
      const authoring = this.options.authoring as AnyAnnotationAuthoringDriver;
      removalOperation = this.startOperation("remove");
      try {
        const result = await authoring.deleteAnnotationFrame(
          {
            collectionId: this.snapshot.workspace.collectionId,
            workspaceRevision: this.snapshot.workspace.revision,
            draftId: draft.draftId,
            base,
            frameId,
          },
          { signal: removalOperation.signal },
        );
        if (
          !this.isCurrentOperation("remove", removalOperation.serial) ||
          isAborted(removalOperation.signal)
        ) {
          return;
        }
        const indexedReceipt = isSavedFrameMutationReceipt(result.entity);
        const nextBase = indexedReceipt
          ? result.entity.set
          : ({ setId: result.entity.setId, revision: result.entity.revision } as const);
        if (indexedReceipt) {
          this.assertSavedFrameIndexRevision(result.entity.savedFrameIndexRevision, "remove");
        }
        const latestDraft = this.snapshot.draft;
        if (!latestDraft || latestDraft.draftId !== draft.draftId || !latestDraft.frames[frameId]) {
          if (nextBase) this.forgetSavedFrame(frameId, nextBase);
          this.materializedFrameRevisions.delete(frameId);
          this.clearFrameHistory(frameId);
          this.clearSaveConflict(frameId);
          this.finishOperation("remove", removalOperation.serial);
          if (
            (this.snapshot.draft?.orderedFrameIds.length ?? 0) > 0 ||
            (this.snapshot.savedFrameIndex?.orderedFrames.length ?? 0) > 0
          ) {
            void this.validateAnnotationSet();
          }
          return;
        }
        draft = nextBase ? { ...latestDraft, base: nextBase } : latestDraft;
        if (nextBase) this.forgetSavedFrame(frameId, nextBase);
      } catch (error) {
        this.failOperation("remove", removalOperation, error);
        return;
      }
    }

    this.stopTrackingPreview();
    const removingActiveFrame = draft.activeFrameId === frameId;
    const removedFrame = draft.frames[frameId];
    const orderedFrameIds = draft.orderedFrameIds.filter((candidate) => candidate !== frameId);
    const frames = { ...draft.frames };
    delete frames[frameId];
    if (removedFrame) this.forgetFrameMaterialization(frameId, removedFrame, frames);
    const hasRemainingFrames = orderedFrameIds.length > 0;
    const cameraFrameIdsBeforeRemoval = removedFrame
      ? draft.orderedFrameIds.filter((candidate) => {
          const candidateFrame = draft.frames[candidate];
          return candidateFrame?.streamId === removedFrame.streamId;
        })
      : draft.orderedFrameIds;
    const remainingCameraFrameIds = cameraFrameIdsBeforeRemoval.filter(
      (candidate) => candidate !== frameId,
    );
    const removedCameraFrameIndex = cameraFrameIdsBeforeRemoval.indexOf(frameId);
    const nextFrameIndex = Math.min(
      Math.max(removedCameraFrameIndex, 0),
      remainingCameraFrameIds.length - 1,
    );
    const activeFrameId = removingActiveFrame
      ? (remainingCameraFrameIds[nextFrameIndex] ?? null)
      : draft.activeFrameId;
    const activeFrame = activeFrameId === null ? undefined : frames[activeFrameId];
    const remainingDirtyScopes = draft.dirtyScopes.filter(
      (scope) => scope.kind === "set" || scope.frameId !== frameId,
    );
    const dirtyScopes = remainingDirtyScopes;

    this.clearFrameHistory(frameId);
    this.patchSnapshot({
      draft: hasRemainingFrames
        ? {
            ...draft,
            orderedFrameIds,
            frames,
            activeFrameId,
            dirtyScopes,
          }
        : null,
      isDirty: hasRemainingFrames && dirtyScopes.length > 0,
      saveConflictFrameIds: (this.snapshot.saveConflictFrameIds ?? []).filter(
        (candidate) => candidate !== frameId,
      ),
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
      selectedSequenceId:
        removingActiveFrame && this.snapshot.viewMode === "annotate"
          ? (activeFrame?.sequenceId ?? this.snapshot.selectedSequenceId)
          : this.snapshot.selectedSequenceId,
      selectedStreamId:
        removingActiveFrame && this.snapshot.viewMode === "annotate"
          ? (activeFrame?.streamId ?? this.snapshot.selectedStreamId)
          : this.snapshot.selectedStreamId,
      viewMode: activeFrame && this.snapshot.viewMode === "annotate" ? "annotate" : "browse",
    });
    if (removalOperation) this.finishOperation("remove", removalOperation.serial);
    // A local-only frame changes the same validation input as a persisted frame. Revalidate every
    // successful removal so the existing c6 conflict indicators describe the remaining set
    // instead of leaving a stale result behind. The final-frame path already resets to idle.
    if (this.snapshot.draft) void this.validateAnnotationSet();
    void this.restoreActiveResources();
  };

  setActiveCapturedFrame = (frameId: string): void => {
    this.assertNotDisposed();
    const draft = this.snapshot.draft;
    const frame = draft?.frames[frameId];
    if (!draft || !frame || !draft.orderedFrameIds.includes(frameId)) return;
    this.resetSegmentationOnContextChange();
    this.stopTrackingPreview();
    const annotating = this.snapshot.viewMode === "annotate";
    this.patchSnapshot({
      ...(annotating
        ? { selectedSequenceId: frame.sequenceId, selectedStreamId: frame.streamId }
        : {}),
      draft: { ...draft, activeFrameId: frameId },
    });
    void this.restoreActiveResources();
  };

  selectCapturedFrame = (frameId: string): void => {
    this.assertNotDisposed();
    const draft = this.snapshot.draft;
    const frame = draft?.frames[frameId];
    if (!draft || !frame || !draft.orderedFrameIds.includes(frameId)) return;
    this.resetSegmentationOnContextChange();
    this.stopTrackingPreview();
    this.patchSnapshot({
      selectedSequenceId: frame.sequenceId,
      selectedStreamId: frame.streamId,
      draft: { ...draft, activeFrameId: frameId },
      viewMode: "annotate",
    });
    void this.restoreActiveResources();
  };

  showCapturedFrame = (): void => {
    this.assertNotDisposed();
    const draft = this.snapshot.draft;
    if (draft === null || draft.activeFrameId === null) return;
    const frame = draft.frames[draft.activeFrameId];
    if (!frame) return;
    this.resetSegmentationOnContextChange();
    this.patchSnapshot({
      selectedSequenceId: frame.sequenceId,
      selectedStreamId: frame.streamId,
      viewMode: "annotate",
    });
    void this.restoreActiveResources();
  };

  showVideo = (): void => {
    this.assertNotDisposed();
    this.resetSegmentationOnContextChange();
    this.stopTrackingPreview();
    this.patchSnapshot({ viewMode: "browse" });
    void this.restoreActiveResources();
  };

  addPoint = (kind: AnnotationPointKind, x: number, y: number): void => {
    this.assertAuthoring("segment");
    const context = this.activeTargetContext();
    if (!context) return;
    const point: NormalizedAnnotationPoint = {
      id: `point-${++this.pointSerial}`,
      kind,
      x: clampCoordinate(x),
      y: clampCoordinate(y),
    };
    this.replaceActiveTarget({
      ...context.target,
      points: [...context.target.points, point],
      pointRevision: context.target.pointRevision + 1,
      contours: undefined,
      maskSource: undefined,
    });
  };

  removePoint = (pointId: string): void => {
    this.assertAuthoring("segment");
    const context = this.activeTargetContext();
    if (!context || !context.target.points.some((point) => point.id === pointId)) return;
    this.replaceActiveTarget({
      ...context.target,
      points: context.target.points.filter((point) => point.id !== pointId),
      pointRevision: context.target.pointRevision + 1,
      contours: undefined,
      maskSource: undefined,
    });
  };

  undoPoint = (): void => {
    this.assertAuthoring("segment");
    const context = this.activeTargetContext();
    if (!context || context.target.points.length === 0) return;
    this.replaceActiveTarget({
      ...context.target,
      points: context.target.points.slice(0, -1),
      pointRevision: context.target.pointRevision + 1,
      contours: undefined,
      maskSource: undefined,
    });
  };

  undo = (): void => {
    this.assertAuthoring("segment");
    const entry = this.undoStack.pop();
    if (!entry) return;
    this.redoStack.push(entry);
    if (this.applyHistoryEntry(entry, "before")) return;
    this.redoStack.pop();
    this.clearHistory();
    this.patchSnapshot({ canUndo: false, canRedo: false });
  };

  redo = (): void => {
    this.assertAuthoring("segment");
    const entry = this.redoStack.pop();
    if (!entry) return;
    this.undoStack.push(entry);
    if (this.applyHistoryEntry(entry, "after")) return;
    this.undoStack.pop();
    this.clearHistory();
    this.patchSnapshot({ canUndo: false, canRedo: false });
  };

  clearTarget = (targetId: string): void => {
    this.assertAuthoring("segment");
    if (!this.snapshot.workspace.targets.some((target) => target.id === targetId)) return;
    const draft = this.snapshot.draft;
    const frameId = draft?.activeFrameId;
    if (!draft || frameId === null || frameId === undefined) return;
    const target = draft.frames[frameId]?.targets[targetId];
    if (!target || !hasAnnotationTargetContent(target)) return;
    this.replaceActiveTarget({
      targetId,
      points: [],
      pointRevision: target.pointRevision + 1,
    });
  };

  clearAllTargets = (): void => {
    this.assertAuthoring("segment");
    this.stopTrackingPreview();
    const draft = this.snapshot.draft;
    const frameId = draft?.activeFrameId;
    if (!draft || frameId === null || frameId === undefined) return;
    const frame = draft.frames[frameId];
    if (!frame) return;
    const populatedTargets = this.snapshot.workspace.targets
      .map((workspaceTarget) => frame.targets[workspaceTarget.id])
      .filter(
        (target): target is FrameTargetAnnotationDraft =>
          target !== undefined && hasAnnotationTargetContent(target),
      );
    if (populatedTargets.length === 0) return;

    const clearedTargetIds = new Set(populatedTargets.map((target) => target.targetId));
    const targets = { ...frame.targets };
    const changes: TargetHistoryChange[] = [];
    for (const target of populatedTargets) {
      const clearedTarget: FrameTargetAnnotationDraft = {
        targetId: target.targetId,
        points: [],
        pointRevision: target.pointRevision + 1,
      };
      targets[target.targetId] = clearedTarget;
      changes.push({ targetId: target.targetId, before: target, after: clearedTarget });
    }
    const dirtyTargetIds = populatedTargets.flatMap((target) =>
      this.targetMatchesSavedBaseline(draft, frameId, targets[target.targetId])
        ? []
        : [target.targetId],
    );
    const dirtyScopes = [
      ...draft.dirtyScopes.filter(
        (scope) =>
          !(
            scope.kind === "target" &&
            scope.frameId === frameId &&
            clearedTargetIds.has(scope.targetId)
          ),
      ),
      ...dirtyTargetIds.map((targetId) => ({ kind: "target", frameId, targetId }) as const),
    ];
    this.recordHistory({
      frameId,
      changes,
      beforeDirtyTargetIds: this.dirtyTargetIds(draft, frameId, [...clearedTargetIds]),
      afterDirtyTargetIds: dirtyTargetIds,
    });
    this.patchSnapshot({
      draft: {
        ...draft,
        frames: { ...draft.frames, [frameId]: { ...frame, targets } },
        dirtyScopes,
      },
      isDirty: dirtyScopes.length > 0,
      canUndo: true,
      canRedo: false,
    });
    void this.restoreActiveResources();
  };

  discardFrameChanges = (frameId: string): void => {
    this.assertAuthoring("save");
    const draft = this.snapshot.draft;
    const frame = draft?.frames[frameId];
    const hasDirtyTargets =
      draft?.dirtyScopes.some((scope) => scope.kind === "target" && scope.frameId === frameId) ??
      false;
    if (
      !draft ||
      !frame ||
      !hasDirtyTargets ||
      this.snapshot.operations.capture.pending ||
      this.snapshot.operations.save.pending
    ) {
      return;
    }

    this.stopTrackingPreview();
    this.operationControllers.get("segment")?.abort();
    this.operationControllers.delete("segment");
    this.clearFrameHistory(frameId);

    const baseline = this.savedDraftBaseline;
    const baselineMatchesDraft =
      baseline?.draftId === draft.draftId &&
      ((baseline.base === undefined && draft.base === undefined) ||
        (baseline.base !== undefined &&
          draft.base !== undefined &&
          baseline.base.setId === draft.base.setId));
    const restoredFrame = {
      ...frame,
      targets: baselineMatchesDraft ? (baseline.frames[frameId]?.targets ?? {}) : {},
    };
    const dirtyScopes = draft.dirtyScopes.filter(
      (scope) => !(scope.kind === "target" && scope.frameId === frameId),
    );

    this.patchSnapshot({
      draft: {
        ...draft,
        frames: { ...draft.frames, [frameId]: restoredFrame },
        dirtyScopes,
      },
      isDirty: dirtyScopes.length > 0,
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
      operations: {
        ...this.snapshot.operations,
        segment: idleOperation(),
        save: idleOperation(),
      },
      saveConflictFrameIds: (this.snapshot.saveConflictFrameIds ?? []).filter(
        (candidate) => candidate !== frameId,
      ),
    });
    void this.restoreActiveResources();
  };

  clearCurrentTarget = (): void => {
    this.assertAuthoring("segment");
    const targetId = this.snapshot.selectedTargetId;
    if (targetId !== null) this.clearTarget(targetId);
  };

  segment = async (): Promise<void> => {
    this.assertNotDisposed();
    if (!resolveSegmentationDriver(this.options)) this.assertAuthoring("segment");
    const context = this.activeTargetContext();
    if (!context) return;
    await this.segmentTarget(context.frame.frameId, context.target.targetId);
  };

  segmentTarget = async (frameId: string, targetId: string): Promise<void> => {
    this.assertNotDisposed();
    const segmentation = resolveSegmentationDriver(this.options);
    if (!segmentation) {
      this.assertAuthoring("segment");
      return;
    }
    const draft = this.snapshot.draft;
    const frame = draft?.frames[frameId];
    const target = frame?.targets[targetId];
    if (!draft || !frame || !target || target.points.length === 0) return;
    if (!segmentation.getAvailability(this.operationContext()).allowed) return;
    const requestTarget = {
      targetId: target.targetId,
      pointRevision: target.pointRevision,
      points: target.points,
    } satisfies SegmentTargetsRequestTarget;
    const operation = this.startOperation("segment");
    try {
      const result = await segmentation.segment(
        {
          frameId: frame.frameId,
          sourceRevision: frame.sourceRevision,
          ...requestTarget,
        },
        { signal: operation.signal },
      );
      if (!this.isCurrentOperation("segment", operation.serial) || isAborted(operation.signal)) {
        return;
      }
      this.applySegmentationResults(
        operation.serial,
        frame.frameId,
        frame.sourceRevision,
        [requestTarget],
        [result],
      );
    } catch (error) {
      this.failOperation("segment", operation, error);
    }
  };

  segmentTargets = async (frameId: string, targetIds: readonly string[]): Promise<void> => {
    this.assertNotDisposed();
    const segmentation = resolveSegmentationDriver(this.options);
    if (!segmentation) {
      this.assertAuthoring("segment");
      return;
    }
    const draft = this.snapshot.draft;
    const frame = draft?.frames[frameId];
    if (!draft || !frame) return;

    const seenTargetIds = new Set<string>();
    const requestTargets: SegmentTargetsRequestTarget[] = [];
    for (const targetId of targetIds) {
      if (seenTargetIds.has(targetId)) continue;
      seenTargetIds.add(targetId);
      const target = frame.targets[targetId];
      if (!target || target.points.length === 0) continue;
      requestTargets.push({
        targetId: target.targetId,
        pointRevision: target.pointRevision,
        points: target.points,
      });
    }
    const firstTarget = requestTargets[0];
    if (!firstTarget) return;

    // An omitted batch capability is an intentional compatibility path. Process one target and
    // let the scheduler advance exactly as it did before this additive contract existed.
    if (!segmentation.segmentTargets || requestTargets.length === 1) {
      await this.segmentTarget(frameId, firstTarget.targetId);
      return;
    }
    if (!segmentation.getAvailability(this.operationContext()).allowed) return;

    const operation = this.startOperation("segment");
    try {
      const results = await segmentation.segmentTargets(
        {
          frameId: frame.frameId,
          sourceRevision: frame.sourceRevision,
          targets: requestTargets,
        },
        { signal: operation.signal },
      );
      if (!this.isCurrentOperation("segment", operation.serial) || isAborted(operation.signal)) {
        return;
      }
      this.applySegmentationResults(
        operation.serial,
        frame.frameId,
        frame.sourceRevision,
        requestTargets,
        results,
      );
    } catch (error) {
      this.failOperation("segment", operation, error);
    }
  };

  getTrackingPreviewAvailability = (targetMedia: TrackingPreviewTargetMedia) => {
    const driver = this.options.trackingPreview;
    if (!driver || !this.isTrackingTargetMedia(targetMedia)) return { allowed: false };
    const context = this.operationContext(targetMedia);
    return driver.getTargetAvailability?.(context, targetMedia) ?? driver.getAvailability(context);
  };

  startTrackingPreview = async (targetMedia?: TrackingPreviewTargetMedia): Promise<void> => {
    this.assertNotDisposed();
    if (this.resumePausedTrackingPreview(targetMedia)) return;
    const driver = this.options.trackingPreview;
    const draft = this.snapshot.draft;
    const canonicalConditioning = driver?.conditioningSource === "canonical";
    const activeFrameId = draft?.activeFrameId;
    const activeFrame = activeFrameId ? draft?.frames[activeFrameId] : undefined;
    const pausedTargetMedia =
      this.snapshot.trackingPreview.status === "stopped"
        ? this.snapshot.trackingPreview.targetMedia
        : null;
    const resolvedTargetMedia =
      targetMedia ??
      pausedTargetMedia ??
      (activeFrame
        ? { sequenceId: activeFrame.sequenceId, streamId: activeFrame.streamId }
        : undefined);
    if (
      !driver ||
      (!draft && !canonicalConditioning) ||
      !resolvedTargetMedia ||
      !this.isTrackingTargetMedia(resolvedTargetMedia) ||
      !(
        driver.getTargetAvailability?.(
          this.operationContext(resolvedTargetMedia),
          resolvedTargetMedia,
        ) ?? driver.getAvailability(this.operationContext(resolvedTargetMedia))
      ).allowed ||
      this.snapshot.operations.segment.pending ||
      this.snapshot.operations.save.pending ||
      (draft !== null &&
        hasBlockingDirtyTrackingTargets(
          draft,
          resolvedTargetMedia.streamId,
          trackingConflictFrameIds(this.snapshot),
        ))
    ) {
      return;
    }

    const validationState = this.snapshot.annotationSetValidation;
    if (
      validationState &&
      (validationState.status === "validating" ||
        validationState.status === "error" ||
        !validationState.availability.allowed)
    ) {
      return;
    }

    const conditioningKeyframes = canonicalConditioning
      ? []
      : createPersistedTrackingConditioningKeyframes(
          this.snapshot.workspace,
          draft as AnnotationSetDraft,
          resolvedTargetMedia.streamId,
        );
    if (!canonicalConditioning && conditioningKeyframes.length === 0) {
      return;
    }
    const conditioningRevision = canonicalConditioning
      ? driver.getConditioningRevision?.(
          this.operationContext(resolvedTargetMedia),
          resolvedTargetMedia,
        )
      : undefined;

    this.releaseTrackingPause();
    this.closeTrackingIterator();
    this.clearTrackingPreviewSession();
    this.trackingSessionAbort = new AbortController();
    const operation = this.startOperation("track", false);
    const targetContextChanged =
      this.snapshot.selectedSequenceId !== resolvedTargetMedia.sequenceId ||
      this.snapshot.selectedStreamId !== resolvedTargetMedia.streamId;
    if (targetContextChanged) this.resetSegmentationOnContextChange();
    this.patchSnapshot({
      ...(targetContextChanged
        ? {
            selectedSequenceId: resolvedTargetMedia.sequenceId,
            selectedStreamId: resolvedTargetMedia.streamId,
            ...(draft ? { draft: { ...draft, activeFrameId: null } } : {}),
            viewMode: "browse" as const,
          }
        : {}),
      operations: {
        ...this.snapshot.operations,
        track: { pending: true, error: null },
      },
      trackingPreview: {
        status: "starting",
        targetMedia: resolvedTargetMedia,
        framesSeen: 0,
        latestFrame: null,
        latestFrameSourceRef: null,
        error: null,
      },
    });
    if (targetContextChanged) void this.restoreActiveResources();

    let iterator: AsyncIterator<TrackingPreviewStreamFrame>;
    try {
      const trackingFrames = driver.previewTracking(
        {
          targetMedia: resolvedTargetMedia,
          conditioningKeyframes,
          ...(conditioningRevision !== undefined ? { conditioningRevision } : {}),
        },
        { signal: operation.signal },
      );
      iterator = trackingFrames[Symbol.asyncIterator]();
    } catch (error) {
      const normalized = normalizeOperationError("track", error);
      this.failOperation("track", operation, normalized);
      this.clearTrackingPreviewSession();
      this.patchSnapshot({
        trackingPreview: {
          ...this.snapshot.trackingPreview,
          status: "error",
          framesSeen: 0,
          latestFrame: null,
          latestFrameSourceRef: null,
          error: normalized,
        },
      });
      return;
    }
    this.activeTrackingIterator = iterator;

    try {
      while (true) {
        await this.waitForTrackingResume(operation.signal);
        if (operation.signal.aborted || !this.isCurrentOperation("track", operation.serial)) {
          break;
        }
        const next = await iterator.next();
        await this.waitForTrackingResume(operation.signal);
        if (operation.signal.aborted || !this.isCurrentOperation("track", operation.serial)) {
          break;
        }
        if (
          conditioningRevision !== undefined &&
          !Object.is(
            conditioningRevision,
            driver.getConditioningRevision?.(
              this.operationContext(resolvedTargetMedia),
              resolvedTargetMedia,
            ),
          )
        ) {
          this.stopTrackingPreview();
          return;
        }
        if (next.done) break;
        const metadata = trackingPreviewMetadata(next.value);
        this.retainLatestTrackingFrameDescriptor(next.value.frameSource);
        this.patchSnapshot({
          trackingPreview: {
            status: "running",
            targetMedia: resolvedTargetMedia,
            framesSeen: this.snapshot.trackingPreview.framesSeen + 1,
            latestFrame: metadata,
            latestFrameSourceRef: next.value.frameSource ? sourceRef(next.value.frameSource) : null,
            error: null,
          },
        });
      }
      if (!operation.signal.aborted && this.isCurrentOperation("track", operation.serial)) {
        this.finishOperation("track", operation.serial);
        this.patchSnapshot({
          trackingPreview: {
            ...this.snapshot.trackingPreview,
            status: "complete",
          },
        });
        // Completion returns presentation ownership to the original media immediately. Keep only
        // lightweight terminal metadata for Run again; the decoded preview frame is no longer shown.
        this.clearTrackingPreviewSession();
      }
    } catch (error) {
      if (!operation.signal.aborted && this.isCurrentOperation("track", operation.serial)) {
        const normalized = normalizeOperationError("track", error);
        this.failOperation("track", operation, normalized);
        this.clearTrackingPreviewSession();
        this.patchSnapshot({
          trackingPreview: {
            ...this.snapshot.trackingPreview,
            status: "error",
            framesSeen: 0,
            latestFrame: null,
            latestFrameSourceRef: null,
            error: normalized,
          },
        });
      }
    } finally {
      if (this.activeTrackingIterator === iterator) this.activeTrackingIterator = null;
    }
  };

  pauseTrackingPreview = (): void => {
    this.assertNotDisposed();
    const status = this.snapshot.trackingPreview.status;
    const operation = this.operationControllers.get("track");
    if (
      (status !== "starting" && status !== "running") ||
      !operation ||
      operation.signal.aborted ||
      this.snapshot.trackingPreview.targetMedia === null
    ) {
      return;
    }

    this.ensureTrackingPause();
    this.patchSnapshot({
      operations: {
        ...this.snapshot.operations,
        track: idleOperation(),
      },
      trackingPreview: {
        ...this.snapshot.trackingPreview,
        status: "stopped",
        error: null,
      },
    });
  };

  stopTrackingPreview = (): void => {
    this.assertNotDisposed();
    const controller = this.operationControllers.get("track");
    if (!controller && this.snapshot.trackingPreview.status === "idle") return;
    controller?.abort();
    this.operationControllers.delete("track");
    this.releaseTrackingPause();
    this.closeTrackingIterator();
    this.clearTrackingPreviewSession();
    this.setOperation("track", idleOperation());
    this.patchSnapshot({
      trackingPreview: {
        ...this.snapshot.trackingPreview,
        status: "stopped",
        targetMedia: null,
        framesSeen: 0,
        latestFrame: null,
        latestFrameSourceRef: null,
        error: null,
      },
    });
  };

  saveAnnotationFrame = async (
    frameId: string,
    options: { readonly validateAfterSave?: boolean } = {},
  ): Promise<void> => {
    this.assertAuthoring("save");
    const draft = this.snapshot.draft;
    const frame = draft?.frames[frameId];
    const frameIndex = draft?.orderedFrameIds.indexOf(frameId) ?? -1;
    const frameHasAnnotationChanges =
      draft?.dirtyScopes.some((scope) => scope.kind === "target" && scope.frameId === frameId) ??
      false;
    if (!draft || !frame || frameIndex < 0 || !frameHasAnnotationChanges) return;
    if (!this.getAuthoringAvailability().allowed) return;
    const authoring = this.options.authoring as AnyAnnotationAuthoringDriver;
    const operation = this.startOperation("save");
    let shouldValidateAnnotationSet = false;
    try {
      const result = await authoring.saveAnnotationFrame(
        {
          collectionId: this.snapshot.workspace.collectionId,
          workspaceRevision: this.snapshot.workspace.revision,
          draftId: draft.draftId,
          base: draft.base,
          frameIndex,
          frame,
        },
        { signal: operation.signal },
      );
      if (!this.isCurrentOperation("save", operation.serial) || isAborted(operation.signal)) return;
      const latestDraft = this.snapshot.draft;
      if (latestDraft?.draftId === draft.draftId) {
        const base = isSavedFrameMutationReceipt(result.entity)
          ? result.entity.set
          : ({ setId: result.entity.setId, revision: result.entity.revision } as const);
        if (isSavedFrameMutationReceipt(result.entity)) {
          this.assertSavedFrameIndexRevision(result.entity.savedFrameIndexRevision, "save");
          const savedSummary = this.findSavedFrameSummary(frameId);
          if (savedSummary) {
            this.materializedFrameRevisions.set(frameId, savedSummary.revision);
            this.touchMaterializedFrame(frameId);
          }
        }
        const frameUnchanged = latestDraft.frames[frameId] === frame;
        if (base) this.rememberSavedFrame(draft, frameId, frame, base);
        else this.rememberMaterializedFrame(draft, frameId, frame);
        if (frameUnchanged) this.clearFrameHistory(frameId);
        const dirtyScopes = frameUnchanged
          ? latestDraft.dirtyScopes.filter(
              (scope) => scope.kind === "set" || scope.frameId !== frameId,
            )
          : latestDraft.dirtyScopes;
        this.patchSnapshot({
          draft: { ...latestDraft, ...(base ? { base } : {}), dirtyScopes },
          isDirty: dirtyScopes.length > 0,
          canUndo: this.undoStack.length > 0,
          canRedo: this.redoStack.length > 0,
        });
        shouldValidateAnnotationSet = frameUnchanged;
      }
      this.clearSaveConflict(frameId);
      this.finishOperation("save", operation.serial);
      if (shouldValidateAnnotationSet && options.validateAfterSave !== false) {
        void this.validateAnnotationSet();
      }
    } catch (error) {
      this.failOperation("save", operation, error, frameId);
    }
  };

  validateAnnotationSet = async (): Promise<void> => {
    this.assertNotDisposed();
    const validation = this.options.annotationSetValidation;
    const draft = this.snapshot.draft;
    const canonical = validation?.validationSource === "canonical";
    if (
      !validation ||
      (!canonical && (!draft || draft.orderedFrameIds.length === 0)) ||
      (canonical && !draft && (this.snapshot.savedFrameIndex?.orderedFrames.length ?? 0) === 0)
    ) {
      return;
    }

    const availability = validation.getAvailability(this.operationContext());
    if (!availability.allowed) {
      this.patchSnapshot({
        annotationSetValidation: {
          status: "idle",
          availability,
          issues: [],
          error: null,
        },
      });
      return;
    }

    this.annotationSetValidationController?.abort();
    const controller = new AbortController();
    const serial = ++this.annotationSetValidationSerial;
    const inputIdentity = annotationSetValidationInputIdentity(this.snapshot, canonical);
    const sourceRevision = canonical
      ? validation.getSourceRevision?.(this.operationContext())
      : undefined;
    this.annotationSetValidationController = controller;
    this.patchSnapshot({
      annotationSetValidation: {
        status: "validating",
        availability,
        issues: [],
        error: null,
        ...(sourceRevision !== undefined ? { sourceRevision } : {}),
      },
    });

    try {
      const result = await validation.validateAnnotationSet(
        {
          collectionId: this.snapshot.workspace.collectionId,
          workspaceRevision: this.snapshot.workspace.revision,
          draftId:
            draft?.draftId ??
            `canonical:${this.snapshot.savedFrameIndex?.set?.setId ?? this.snapshot.workspace.collectionId}`,
          base: draft?.base ?? this.snapshot.savedFrameIndex?.set ?? undefined,
          targetIds: this.snapshot.workspace.targets.map((target) => target.id),
          frames: (draft?.orderedFrameIds ?? []).flatMap((frameId) => {
            const frame = draft?.frames[frameId];
            return frame ? [frame] : [];
          }),
          ...(sourceRevision !== undefined ? { sourceRevision } : {}),
        },
        { signal: controller.signal },
      );
      if (
        controller.signal.aborted ||
        serial !== this.annotationSetValidationSerial ||
        (sourceRevision !== undefined &&
          !Object.is(sourceRevision, validation.getSourceRevision?.(this.operationContext()))) ||
        !annotationSetValidationInputsMatch(
          inputIdentity,
          annotationSetValidationInputIdentity(this.snapshot, canonical),
        )
      ) {
        return;
      }
      this.patchSnapshot({
        annotationSetValidation: {
          status: result.ready ? "ready" : "blocked",
          availability: validation.getAvailability(this.operationContext()),
          issues: result.issues,
          error: null,
          ...(sourceRevision !== undefined ? { sourceRevision } : {}),
        },
      });
    } catch (error) {
      if (controller.signal.aborted || serial !== this.annotationSetValidationSerial) return;
      this.patchSnapshot({
        annotationSetValidation: {
          status: "error",
          availability: validation.getAvailability(this.operationContext()),
          issues: [],
          error: error instanceof Error ? error.message : "unknown",
        },
      });
    } finally {
      if (serial === this.annotationSetValidationSerial) {
        this.annotationSetValidationController = null;
      }
    }
  };

  private disconnect(): void {
    this.sourceUnsubscribe?.();
    this.sourceUnsubscribe = null;
    this.directoryUnsubscribe?.();
    this.directoryUnsubscribe = null;
    this.savedFrameIndexUnsubscribe?.();
    this.savedFrameIndexUnsubscribe = null;
    this.ensureSequenceController?.abort();
    this.ensureSequenceController = null;
    this.ensureSequenceSerial += 1;
    this.materializeFrameController?.abort();
    this.materializeFrameController = null;
    this.materializeFrameSerial += 1;
    this.connectionAbort?.abort();
    this.connectionAbort = null;
    this.pendingResources.clear();
    for (const controller of this.operationControllers.values()) controller.abort();
    this.operationControllers.clear();
    this.annotationSetValidationController?.abort();
    this.annotationSetValidationController = null;
    this.annotationSetValidationSerial += 1;
    this.closeTrackingIterator();
    this.clearTrackingPreviewSession();
    this.disposeActiveResources();
    if (!this.disposed && this.snapshot.connected) {
      this.patchSnapshot({
        connected: false,
        operations: initialOperations(),
        annotationSetValidation:
          this.snapshot.annotationSetValidation?.status === "validating"
            ? {
                ...this.snapshot.annotationSetValidation,
                status: "stale",
                issues: [],
                error: null,
              }
            : this.snapshot.annotationSetValidation,
        trackingPreview:
          this.snapshot.trackingPreview.status === "idle"
            ? {
                ...this.snapshot.trackingPreview,
                framesSeen: 0,
                latestFrame: null,
                latestFrameSourceRef: null,
              }
            : {
                ...this.snapshot.trackingPreview,
                status: "stopped",
                targetMedia: null,
                framesSeen: 0,
                latestFrame: null,
                latestFrameSourceRef: null,
                error: null,
              },
      });
    }
  }

  private settlePendingLeave(allowed: boolean): void {
    const pending = this.pendingLeaveRequest;
    if (!pending) return;
    this.pendingLeaveRequest = null;
    this.patchSnapshot({ leaveConfirmation: null });
    pending.resolve(allowed);
  }

  private resetSegmentationOnContextChange(): void {
    this.operationControllers.get("segment")?.abort();
    this.operationControllers.delete("segment");
    const operation = this.snapshot.operations.segment;
    if (operation.pending || operation.error !== null) {
      this.setOperation("segment", idleOperation());
    }
  }

  private closeTrackingIterator(): void {
    const iterator = this.activeTrackingIterator;
    this.activeTrackingIterator = null;
    if (!iterator?.return) return;
    try {
      void Promise.resolve(iterator.return()).catch(() => undefined);
    } catch {
      // Iterator teardown is best-effort after the operation signal has already been aborted.
    }
  }

  private ensureTrackingPause(): void {
    if (this.trackingPausePromise) return;
    this.trackingPausePromise = new Promise<void>((resolve) => {
      this.resumeTrackingProducer = resolve;
    });
  }

  private releaseTrackingPause(): void {
    const resume = this.resumeTrackingProducer;
    this.resumeTrackingProducer = null;
    this.trackingPausePromise = null;
    resume?.();
  }

  private async waitForTrackingResume(signal: AbortSignal): Promise<void> {
    while (this.trackingPausePromise && !signal.aborted) {
      await this.trackingPausePromise;
    }
  }

  private resumePausedTrackingPreview(targetMedia?: TrackingPreviewTargetMedia): boolean {
    const preview = this.snapshot.trackingPreview;
    const pausedTarget = preview.targetMedia;
    const requestedTarget = targetMedia ?? pausedTarget;
    const operation = this.operationControllers.get("track");
    if (
      preview.status !== "stopped" ||
      pausedTarget === null ||
      requestedTarget === null ||
      requestedTarget === undefined ||
      requestedTarget.sequenceId !== pausedTarget.sequenceId ||
      requestedTarget.streamId !== pausedTarget.streamId ||
      !this.activeTrackingIterator ||
      !operation ||
      operation.signal.aborted ||
      !this.trackingPausePromise
    ) {
      return false;
    }

    this.patchSnapshot({
      operations: {
        ...this.snapshot.operations,
        track: { pending: true, error: null },
      },
      trackingPreview: {
        ...preview,
        status: "running",
        error: null,
      },
    });
    this.releaseTrackingPause();
    return true;
  }

  private reconcileWorkspace(workspace: AnnotationWorkspaceSnapshot): void {
    if (workspace === this.snapshot.workspace) return;
    const asyncDirectory = this.snapshot.sequenceDirectory ?? null;
    const currentSequence = resolveSequence(
      workspace,
      this.snapshot.selectedSequenceId,
      asyncDirectory,
      this.options.sequenceDirectory?.getCachedSequence.bind(this.options.sequenceDirectory),
    );
    const selectedSequenceId = currentSequence
      ? currentSequence.id
      : asyncDirectory
        ? (asyncDirectory.items[0]?.id ?? this.snapshot.selectedSequenceId)
        : (workspace.sequences[0]?.id ?? null);
    const selectedSequence = resolveSequence(
      workspace,
      selectedSequenceId,
      asyncDirectory,
      this.options.sequenceDirectory?.getCachedSequence.bind(this.options.sequenceDirectory),
    );
    const selectedStreamId = resolveStreamId(
      workspace,
      selectedSequence,
      this.snapshot.selectedStreamId,
    );
    const selectedTargetId = workspace.targets.some(
      (target) => target.id === this.snapshot.selectedTargetId,
    )
      ? this.snapshot.selectedTargetId
      : (workspace.targets[0]?.id ?? null);
    const collectionChanged = workspace.collectionId !== this.snapshot.workspace.collectionId;
    if (collectionChanged) {
      this.clearHistory();
      this.savedDraftBaseline = null;
    }
    const currentDraft = this.snapshot.draft;
    const draft =
      currentDraft && !collectionChanged && !asyncDirectory
        ? {
            ...currentDraft,
            orderedFrameIds: orderFrameIdsBySequenceAndTime(
              currentDraft.orderedFrameIds,
              currentDraft.frames,
              workspace.sequences.map((sequence) => sequence.id),
            ),
          }
        : currentDraft;
    if (this.savedDraftBaseline && !collectionChanged && !asyncDirectory) {
      this.savedDraftBaseline = {
        ...this.savedDraftBaseline,
        orderedFrameIds: orderFrameIdsBySequenceAndTime(
          this.savedDraftBaseline.orderedFrameIds,
          this.savedDraftBaseline.frames,
          workspace.sequences.map((sequence) => sequence.id),
        ),
      };
    }
    this.patchSnapshot({
      workspace,
      selectedSequenceId,
      selectedStreamId,
      selectedTargetId,
      pinnedSequence: collectionChanged
        ? selectedSequence
          ? this.resolvePinnedSequence(selectedSequence)
          : null
        : this.resolvePinnedSequenceAfterIntent(selectedSequence),
      draft,
      saveConflictFrameIds: collectionChanged ? [] : this.snapshot.saveConflictFrameIds,
      canUndo: collectionChanged ? false : this.snapshot.canUndo,
      canRedo: collectionChanged ? false : this.snapshot.canRedo,
    });
  }

  private reconcileSequenceDirectory(directory: AnnotationSequenceDirectorySnapshot | null): void {
    if (directory === this.snapshot.sequenceDirectory) return;
    if (!directory) {
      this.patchSnapshot({ sequenceDirectory: null, pinnedSequence: null });
      return;
    }

    const previousDirectory = this.snapshot.sequenceDirectory;
    const queryChanged =
      previousDirectory != null &&
      !sameSequenceDirectoryQuery(previousDirectory.query, directory.query);

    const previousSelectedSequence = resolveSequence(
      this.snapshot.workspace,
      this.snapshot.selectedSequenceId,
      this.snapshot.sequenceDirectory ?? null,
      this.options.sequenceDirectory?.getCachedSequence.bind(this.options.sequenceDirectory),
    );
    const currentSequence =
      resolveSequence(
        this.snapshot.workspace,
        this.snapshot.selectedSequenceId,
        directory,
        this.options.sequenceDirectory?.getCachedSequence.bind(this.options.sequenceDirectory),
      ) ??
      previousSelectedSequence ??
      (this.snapshot.pinnedSequence?.id === this.snapshot.selectedSequenceId
        ? this.snapshot.pinnedSequence
        : null);
    const queryStreamId = directory.query.streamId ?? this.snapshot.selectedStreamId;
    const firstScopedSequence = directory.items.find(
      (sequence) => queryStreamId === null || sequence.streamIds.includes(queryStreamId),
    );
    const selectedSequence = currentSequence ?? firstScopedSequence ?? null;
    const selectedSequenceId = selectedSequence?.id ?? this.snapshot.selectedSequenceId;
    const selectedStreamId = selectedSequence
      ? resolveStreamId(this.snapshot.workspace, selectedSequence, this.snapshot.selectedStreamId)
      : this.snapshot.selectedStreamId;
    const selectedPinnedSequence =
      selectedSequence && !directory.items.some((item) => item.id === selectedSequence.id)
        ? selectedSequence
        : null;
    const retainedPinnedSequence =
      !queryChanged &&
      this.snapshot.pinnedSequence &&
      !directory.items.some((item) => item.id === this.snapshot.pinnedSequence?.id)
        ? this.snapshot.pinnedSequence
        : null;

    this.patchSnapshot({
      sequenceDirectory: directory,
      selectedSequenceId,
      selectedStreamId,
      pinnedSequence: selectedPinnedSequence ?? retainedPinnedSequence,
    });
  }

  private reconcileSavedFrameIndex(index: AnnotationSavedFrameIndexSnapshot | null): void {
    if (index === this.snapshot.savedFrameIndex) return;
    if (!index) {
      this.materializedFrameRevisions.clear();
      this.materializedFrameAccess.clear();
      this.patchSnapshot({ savedFrameIndex: null });
      return;
    }

    const summaries = new Map(index.orderedFrames.map((frame) => [frame.frameId, frame]));
    const draft = this.snapshot.draft;
    let nextDraft = draft;
    let nextSaveConflictFrameIds = this.snapshot.saveConflictFrameIds ?? [];
    if (draft) {
      const frames = { ...draft.frames };
      let changed = false;
      for (const [frameId, materializedRevision] of this.materializedFrameRevisions) {
        const summary = summaries.get(frameId);
        if (summary && Object.is(summary.revision, materializedRevision)) continue;
        if (this.frameHasDirtyTargets(frameId)) {
          if (!nextSaveConflictFrameIds.includes(frameId)) {
            nextSaveConflictFrameIds = [...nextSaveConflictFrameIds, frameId];
          }
          continue;
        }
        const removedFrame = frames[frameId];
        delete frames[frameId];
        if (removedFrame) this.forgetFrameMaterialization(frameId, removedFrame, frames);
        else {
          this.materializedFrameRevisions.delete(frameId);
          this.materializedFrameAccess.delete(frameId);
        }
        changed = true;
      }
      if (changed) {
        const orderedFrameIds = draft.orderedFrameIds.filter((frameId) => frames[frameId]);
        nextDraft = {
          ...draft,
          base: index.set ?? draft.base,
          orderedFrameIds,
          frames,
          activeFrameId:
            draft.activeFrameId && frames[draft.activeFrameId] ? draft.activeFrameId : null,
        };
      } else if (
        index.set &&
        (draft.base?.setId !== index.set.setId || draft.base.revision !== index.set.revision)
      ) {
        nextDraft = { ...draft, base: index.set };
      }
    }
    this.patchSnapshot({
      savedFrameIndex: index,
      draft: nextDraft,
      saveConflictFrameIds: nextSaveConflictFrameIds,
      viewMode:
        nextDraft?.activeFrameId === null && this.snapshot.viewMode === "annotate"
          ? "browse"
          : this.snapshot.viewMode,
    });
    if (nextDraft !== draft) void this.restoreActiveResources();
  }

  private findSequence(sequenceId: string | null): AnnotationSequence | null {
    return resolveSequence(
      this.snapshot.workspace,
      sequenceId,
      this.snapshot.sequenceDirectory ?? null,
      this.options.sequenceDirectory?.getCachedSequence.bind(this.options.sequenceDirectory),
    );
  }

  private findSavedFrameSummary(
    frameId: string,
    snapshot: AnnotationSavedFrameIndexSnapshot | null = this.snapshot.savedFrameIndex ?? null,
  ): AnnotationFrameSummary | null {
    if (!snapshot) return null;
    return (
      this.options.savedFrameIndex?.getFrameSummary?.(frameId) ??
      snapshot.orderedFrames.find((frame) => frame.frameId === frameId) ??
      null
    );
  }

  private resolvePinnedSequence(sequence: AnnotationSequence): AnnotationSequence | null {
    const directory = this.snapshot.sequenceDirectory;
    if (!directory || directory.items.some((item) => item.id === sequence.id)) return null;
    return sequence;
  }

  private resolvePinnedSequenceAfterIntent(
    sequence: AnnotationSequence | null,
  ): AnnotationSequence | null {
    const nextPinnedSequence = sequence ? this.resolvePinnedSequence(sequence) : null;
    if (nextPinnedSequence) return nextPinnedSequence;
    const currentPinnedSequence = this.snapshot.pinnedSequence;
    return currentPinnedSequence ? this.resolvePinnedSequence(currentPinnedSequence) : null;
  }

  private createHydrationWorkspace(
    workspace: AnnotationWorkspaceSnapshot,
    session: AnnotationStudioInitialSession,
    directory: AnnotationSequenceDirectorySnapshot | null,
  ): AnnotationWorkspaceSnapshot {
    if (!this.options.sequenceDirectory) return workspace;
    const sequences = new Map(workspace.sequences.map((sequence) => [sequence.id, sequence]));
    for (const sequence of directory?.items ?? []) sequences.set(sequence.id, sequence);
    const sequenceIds = new Set([
      ...session.orderedFrames.map((frame) => frame.sequenceId),
      ...(session.selectedSequenceId ? [session.selectedSequenceId] : []),
    ]);
    for (const sequenceId of sequenceIds) {
      const cached = this.options.sequenceDirectory.getCachedSequence(sequenceId);
      if (cached) sequences.set(cached.id, cached);
    }
    const annotationSets = session.base
      ? workspace.annotationSets.some((set) => set.setId === session.base?.setId)
        ? workspace.annotationSets
        : [
            ...workspace.annotationSets,
            {
              setId: session.base.setId,
              revision: session.base.revision,
              orderedFrames: [],
            },
          ]
      : workspace.annotationSets;
    return { ...workspace, sequences: [...sequences.values()], annotationSets };
  }

  private workspaceWithSequence(
    sequence: AnnotationSequence,
    set: Readonly<{ setId: string; revision: number }> | null,
  ): AnnotationWorkspaceSnapshot {
    const sequences = this.snapshot.workspace.sequences.some((item) => item.id === sequence.id)
      ? this.snapshot.workspace.sequences
      : [...this.snapshot.workspace.sequences, sequence];
    const annotationSets =
      set && !this.snapshot.workspace.annotationSets.some((item) => item.setId === set.setId)
        ? [...this.snapshot.workspace.annotationSets, { ...set, orderedFrames: [] }]
        : this.snapshot.workspace.annotationSets;
    return { ...this.snapshot.workspace, sequences, annotationSets };
  }

  private frameHasDirtyTargets(frameId: string): boolean {
    return (
      this.snapshot.draft?.dirtyScopes.some(
        (scope) => scope.kind === "target" && scope.frameId === frameId,
      ) ?? false
    );
  }

  private touchMaterializedFrame(frameId: string): void {
    this.materializedFrameAccess.set(frameId, ++this.materializedFrameAccessSerial);
  }

  private evictMaterializedFrames(additionalRetainedFrameId?: string): void {
    if (this.materializedFrameRevisions.size <= materializedFrameCacheLimit) return;
    const draft = this.snapshot.draft;
    if (!draft) return;

    const retainedFrameIds = new Set(this.retainedMaterializedFrameIds);
    if (additionalRetainedFrameId) retainedFrameIds.add(additionalRetainedFrameId);
    if (draft.activeFrameId) retainedFrameIds.add(draft.activeFrameId);
    for (const scope of draft.dirtyScopes) {
      if (scope.kind !== "set") retainedFrameIds.add(scope.frameId);
    }
    for (const entry of [...this.undoStack, ...this.redoStack]) {
      retainedFrameIds.add(entry.frameId);
    }

    const candidates = [...this.materializedFrameRevisions.keys()]
      .filter((frameId) => !retainedFrameIds.has(frameId))
      .sort(
        (left, right) =>
          (this.materializedFrameAccess.get(left) ?? 0) -
          (this.materializedFrameAccess.get(right) ?? 0),
      );
    if (candidates.length === 0) return;

    const frames = { ...draft.frames };
    let changed = false;
    for (const frameId of candidates) {
      if (this.materializedFrameRevisions.size <= materializedFrameCacheLimit) break;
      const frame = frames[frameId];
      if (!frame) {
        this.materializedFrameRevisions.delete(frameId);
        this.materializedFrameAccess.delete(frameId);
        continue;
      }
      delete frames[frameId];
      this.forgetFrameMaterialization(frameId, frame, frames);
      changed = true;
    }
    if (!changed) return;

    const orderedFrameIds = draft.orderedFrameIds.filter((frameId) => frames[frameId]);
    const nextDraft = orderedFrameIds.length > 0 ? { ...draft, orderedFrameIds, frames } : null;
    this.patchSnapshot({
      draft: nextDraft,
      isDirty: nextDraft?.dirtyScopes.length ? true : false,
      viewMode:
        nextDraft?.activeFrameId === null && this.snapshot.viewMode === "annotate"
          ? "browse"
          : this.snapshot.viewMode,
    });
    void this.restoreActiveResources();
  }

  private forgetFrameMaterialization(
    frameId: string,
    frame: AnnotationFrameDraft,
    remainingFrames: Readonly<Record<string, AnnotationFrameDraft>>,
  ): void {
    this.materializedFrameRevisions.delete(frameId);
    this.materializedFrameAccess.delete(frameId);
    const baseline = this.savedDraftBaseline;
    if (baseline?.frames[frameId]) {
      const baselineFrames = { ...baseline.frames };
      delete baselineFrames[frameId];
      this.savedDraftBaseline = {
        ...baseline,
        orderedFrameIds: baseline.orderedFrameIds.filter((candidate) => candidate !== frameId),
        frames: baselineFrames,
        activeFrameId: baseline.activeFrameId === frameId ? null : baseline.activeFrameId,
      };
    }

    const retainedDescriptorKeys = new Set<string>();
    for (const remainingFrame of Object.values(remainingFrames)) {
      retainedDescriptorKeys.add(descriptorRegistryKey(remainingFrame.frameSource));
      for (const target of Object.values(remainingFrame.targets)) {
        if (target.maskSource) retainedDescriptorKeys.add(descriptorRegistryKey(target.maskSource));
      }
    }
    const releasedDescriptorKeys = new Set([descriptorRegistryKey(frame.frameSource)]);
    for (const target of Object.values(frame.targets)) {
      if (target.maskSource) releasedDescriptorKeys.add(descriptorRegistryKey(target.maskSource));
    }
    for (const descriptorKey of releasedDescriptorKeys) {
      if (!retainedDescriptorKeys.has(descriptorKey)) this.descriptors.delete(descriptorKey);
    }
  }

  private rememberMaterializedFrame(
    draft: AnnotationSetDraft,
    frameId: string,
    frame: AnnotationFrameDraft,
  ): void {
    const previous =
      this.savedDraftBaseline?.draftId === draft.draftId ? this.savedDraftBaseline : null;
    const frames = { ...(previous?.frames ?? {}), [frameId]: frame };
    this.savedDraftBaseline = {
      draftId: draft.draftId,
      ...(draft.base ? { base: draft.base } : {}),
      orderedFrameIds: draft.orderedFrameIds.filter((candidate) => frames[candidate] !== undefined),
      frames,
      activeFrameId: draft.activeFrameId,
      dirtyScopes: [],
    };
  }

  private assertSavedFrameIndexRevision(
    expectedRevision: AnnotationSourceRevision,
    operation: "save" | "remove",
  ): void {
    const currentRevision = this.options.savedFrameIndex?.getSnapshot().revision;
    if (Object.is(currentRevision, expectedRevision)) return;
    throw new AnnotationOperationError(operation, "saved_frame_index_revision_mismatch", {
      retryable: true,
      conflict: true,
    });
  }

  private replaceActiveTarget(target: FrameTargetAnnotationDraft, markDirty = true): void {
    const draft = this.snapshot.draft;
    const frameId = draft?.activeFrameId;
    if (!draft || frameId === null || frameId === undefined) return;
    this.replaceFrameTarget(frameId, target, markDirty);
  }

  private applySegmentationResults(
    operationSerial: number,
    frameId: string,
    sourceRevision: number,
    requestedTargets: readonly SegmentTargetsRequestTarget[],
    results: readonly SegmentationResult[],
  ): void {
    if (!this.isCurrentOperation("segment", operationSerial)) return;
    const draft = this.snapshot.draft;
    const frame = draft?.frames[frameId];
    if (!draft || !frame || frame.sourceRevision !== sourceRevision) {
      this.finishOperation("segment", operationSerial);
      return;
    }

    const requestByTargetId = new Map(
      requestedTargets.map((target) => [target.targetId, target] as const),
    );
    const appliedTargetIds = new Set<string>();
    const nextTargets = { ...frame.targets };
    let changed = false;

    for (const result of results) {
      const request = requestByTargetId.get(result.targetId);
      const latestTarget = frame.targets[result.targetId];
      if (
        result.frameId !== frameId ||
        !request ||
        !latestTarget ||
        result.pointRevision !== request.pointRevision ||
        latestTarget.pointRevision !== request.pointRevision ||
        appliedTargetIds.has(result.targetId)
      ) {
        continue;
      }

      const nextTarget: FrameTargetAnnotationDraft = {
        targetId: latestTarget.targetId,
        points: latestTarget.points,
        pointRevision: latestTarget.pointRevision,
        ...(result.contours !== undefined ? { contours: result.contours } : {}),
        ...(result.maskSource !== undefined ? { maskSource: sourceRef(result.maskSource) } : {}),
      };
      if (result.maskSource) {
        this.descriptors.set(descriptorRegistryKey(result.maskSource), result.maskSource);
      }
      appliedTargetIds.add(result.targetId);
      nextTargets[result.targetId] = nextTarget;
      this.updateLatestHistoryTarget(frameId, nextTarget);
      changed = true;
    }

    if (!changed) {
      this.finishOperation("segment", operationSerial);
      return;
    }

    // Publish every valid target and the completed operation in one snapshot so consumers never
    // render a half-applied batch or an extra pending-only frame after contours arrive.
    this.operationControllers.delete("segment");
    this.patchSnapshot({
      draft: {
        ...draft,
        frames: {
          ...draft.frames,
          [frameId]: { ...frame, targets: nextTargets },
        },
      },
      operations: {
        ...this.snapshot.operations,
        segment: idleOperation(),
      },
    });
    void this.restoreActiveResources();
  }

  private replaceFrameTarget(
    frameId: string,
    target: FrameTargetAnnotationDraft,
    markDirty = true,
  ): void {
    if (markDirty) this.stopTrackingPreview();
    const draft = this.snapshot.draft;
    if (!draft) return;
    const frame = draft.frames[frameId];
    if (!frame) return;
    const previousTarget = frame.targets[target.targetId];
    const dirtyScope = { kind: "target", frameId, targetId: target.targetId } as const;
    const targetIsDirty = markDirty && !this.targetMatchesSavedBaseline(draft, frameId, target);
    const dirtyScopes = markDirty
      ? [
          ...draft.dirtyScopes.filter(
            (scope) =>
              !(
                scope.kind === "target" &&
                scope.frameId === frameId &&
                scope.targetId === target.targetId
              ),
          ),
          ...(targetIsDirty ? [dirtyScope] : []),
        ]
      : draft.dirtyScopes;
    if (markDirty) {
      this.recordHistory({
        frameId,
        changes: [{ targetId: target.targetId, before: previousTarget, after: target }],
        beforeDirtyTargetIds: this.dirtyTargetIds(draft, frameId, [target.targetId]),
        afterDirtyTargetIds: targetIsDirty ? [target.targetId] : [],
      });
    } else {
      this.updateLatestHistoryTarget(frameId, target);
    }
    this.patchSnapshot({
      draft: {
        ...draft,
        frames: {
          ...draft.frames,
          [frameId]: { ...frame, targets: { ...frame.targets, [target.targetId]: target } },
        },
        dirtyScopes,
      },
      isDirty: markDirty ? dirtyScopes.length > 0 : this.snapshot.isDirty,
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
      operations:
        markDirty &&
        !this.snapshot.operations.segment.pending &&
        this.snapshot.operations.segment.error !== null
          ? { ...this.snapshot.operations, segment: idleOperation() }
          : this.snapshot.operations,
    });
    void this.restoreActiveResources();
  }

  private recordHistory(entry: AnnotationHistoryEntry): void {
    this.undoStack.push(entry);
    if (this.undoStack.length > annotationHistoryLimit) this.undoStack.shift();
    this.redoStack.length = 0;
  }

  private clearHistory(): void {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
  }

  private clearFrameHistory(frameId: string): void {
    const undoEntries = this.undoStack.filter((entry) => entry.frameId !== frameId);
    const redoEntries = this.redoStack.filter((entry) => entry.frameId !== frameId);
    this.undoStack.splice(0, this.undoStack.length, ...undoEntries);
    this.redoStack.splice(0, this.redoStack.length, ...redoEntries);
  }

  private rememberSavedFrame(
    draft: AnnotationSetDraft,
    frameId: string,
    frame: AnnotationFrameDraft,
    base: Readonly<{ setId: string; revision: number }>,
  ): void {
    const previous =
      this.savedDraftBaseline?.draftId === draft.draftId &&
      (this.savedDraftBaseline.base === undefined ||
        this.savedDraftBaseline.base.setId === base.setId)
        ? this.savedDraftBaseline
        : null;
    const frames = { ...(previous?.frames ?? {}), [frameId]: frame };
    this.savedDraftBaseline = {
      draftId: draft.draftId,
      base,
      orderedFrameIds: draft.orderedFrameIds.filter((candidate) => frames[candidate] !== undefined),
      frames,
      activeFrameId: frameId,
      dirtyScopes: [],
    };
  }

  private forgetSavedFrame(
    frameId: string,
    base: Readonly<{ setId: string; revision: number }>,
  ): void {
    const previous = this.savedDraftBaseline;
    if (previous?.base?.setId !== base.setId) return;
    const frames = { ...previous.frames };
    delete frames[frameId];
    this.savedDraftBaseline = {
      ...previous,
      base,
      orderedFrameIds: previous.orderedFrameIds.filter((candidate) => candidate !== frameId),
      frames,
      activeFrameId: previous.activeFrameId === frameId ? null : previous.activeFrameId,
    };
  }

  private dirtyTargetIds(
    draft: AnnotationSetDraft,
    frameId: string,
    targetIds: readonly string[],
  ): string[] {
    const targetIdSet = new Set(targetIds);
    return draft.dirtyScopes.flatMap((scope) =>
      scope.kind === "target" && scope.frameId === frameId && targetIdSet.has(scope.targetId)
        ? [scope.targetId]
        : [],
    );
  }

  private targetMatchesSavedBaseline(
    draft: AnnotationSetDraft,
    frameId: string,
    target: FrameTargetAnnotationDraft,
  ): boolean {
    const baseline = this.savedDraftBaseline;
    const baselineMatchesDraft =
      baseline?.draftId === draft.draftId &&
      ((baseline.base === undefined && draft.base === undefined) ||
        (baseline.base !== undefined &&
          draft.base !== undefined &&
          baseline.base.setId === draft.base.setId));
    if (!baselineMatchesDraft) {
      return draft.base === undefined && !hasAnnotationTargetContent(target);
    }
    return hasSameAnnotationTargetContent(
      target,
      baseline.frames[frameId]?.targets[target.targetId],
    );
  }

  private updateLatestHistoryTarget(frameId: string, target: FrameTargetAnnotationDraft): void {
    for (let index = this.undoStack.length - 1; index >= 0; index -= 1) {
      const entry = this.undoStack[index];
      if (entry.frameId !== frameId) continue;
      const changeIndex = entry.changes.findIndex(
        (change) =>
          change.targetId === target.targetId &&
          change.after?.pointRevision === target.pointRevision,
      );
      if (changeIndex < 0) continue;
      const changes = [...entry.changes];
      changes[changeIndex] = { ...changes[changeIndex], after: target };
      this.undoStack[index] = { ...entry, changes };
      return;
    }
  }

  private applyHistoryEntry(entry: AnnotationHistoryEntry, state: "before" | "after"): boolean {
    this.stopTrackingPreview();
    const draft = this.snapshot.draft;
    const frame = draft?.frames[entry.frameId];
    if (!draft || !frame) return false;

    this.operationControllers.get("segment")?.abort();
    this.operationControllers.delete("segment");
    const targets = { ...frame.targets };
    const affectedTargetIds = new Set(entry.changes.map((change) => change.targetId));
    for (const change of entry.changes) {
      const target = change[state];
      if (target) targets[change.targetId] = target;
      else delete targets[change.targetId];
    }
    const restoredDirtyTargetIds =
      state === "before" ? entry.beforeDirtyTargetIds : entry.afterDirtyTargetIds;
    const dirtyScopes = [
      ...draft.dirtyScopes.filter(
        (scope) =>
          !(
            scope.kind === "target" &&
            scope.frameId === entry.frameId &&
            affectedTargetIds.has(scope.targetId)
          ),
      ),
      ...restoredDirtyTargetIds.map(
        (targetId) => ({ kind: "target", frameId: entry.frameId, targetId }) as const,
      ),
    ];
    const selectedTargetId = affectedTargetIds.has(this.snapshot.selectedTargetId ?? "")
      ? this.snapshot.selectedTargetId
      : (entry.changes[0]?.targetId ?? this.snapshot.selectedTargetId);
    this.patchSnapshot({
      selectedSequenceId: frame.sequenceId,
      selectedStreamId: frame.streamId,
      selectedTargetId,
      viewMode: "annotate",
      draft: {
        ...draft,
        activeFrameId: entry.frameId,
        frames: { ...draft.frames, [entry.frameId]: { ...frame, targets } },
        dirtyScopes,
      },
      isDirty: dirtyScopes.length > 0,
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
      operations: { ...this.snapshot.operations, segment: idleOperation() },
    });
    void this.restoreActiveResources();
    return true;
  }

  private activeTargetContext(): {
    draft: AnnotationSetDraft;
    frame: AnnotationFrameDraft;
    target: FrameTargetAnnotationDraft;
  } | null {
    const draft = this.snapshot.draft;
    const frameId = draft?.activeFrameId;
    const targetId = this.snapshot.selectedTargetId;
    if (!draft || frameId === null || frameId === undefined || targetId === null) return null;
    const frame = draft.frames[frameId];
    if (!frame) return null;
    return {
      draft,
      frame,
      target: frame.targets[targetId] ?? {
        targetId,
        points: [],
        pointRevision: 0,
      },
    };
  }

  private isTrackingTargetMedia(targetMedia: TrackingPreviewTargetMedia): boolean {
    const sequence = this.findSequence(targetMedia.sequenceId);
    return (
      sequence?.streamIds.includes(targetMedia.streamId) === true &&
      this.snapshot.workspace.streams.some((stream) => stream.id === targetMedia.streamId)
    );
  }

  private operationContext(targetMedia?: TrackingPreviewTargetMedia) {
    return {
      workspace: this.snapshot.workspace,
      sequenceId: targetMedia?.sequenceId ?? this.snapshot.selectedSequenceId,
      streamId: targetMedia?.streamId ?? this.snapshot.selectedStreamId,
      targetId: this.snapshot.selectedTargetId,
      draft: this.snapshot.draft,
    };
  }

  private startOperation(
    operation: AnnotationOperation,
    publishSnapshot = true,
  ): {
    serial: number;
    signal: AbortSignal;
  } {
    this.operationControllers.get(operation)?.abort();
    const controller = new AbortController();
    const serial = (this.operationSerial.get(operation) ?? 0) + 1;
    this.operationSerial.set(operation, serial);
    this.operationControllers.set(operation, controller);
    if (publishSnapshot) this.setOperation(operation, { pending: true, error: null });
    return { serial, signal: controller.signal };
  }

  private finishOperation(operation: AnnotationOperation, serial: number): void {
    if (!this.isCurrentOperation(operation, serial)) return;
    this.operationControllers.delete(operation);
    this.setOperation(operation, idleOperation());
  }

  private failOperation(
    operation: AnnotationOperation,
    active: { serial: number; signal: AbortSignal },
    error: unknown,
    conflictFrameId?: string,
  ): void {
    if (!this.isCurrentOperation(operation, active.serial) || active.signal.aborted) return;
    this.operationControllers.delete(operation);
    const normalizedError = normalizeOperationError(operation, error);
    const currentConflictFrameIds = this.snapshot.saveConflictFrameIds ?? [];
    const saveConflictFrameIds =
      operation === "save" &&
      normalizedError.conflict &&
      conflictFrameId !== undefined &&
      !currentConflictFrameIds.includes(conflictFrameId)
        ? [...currentConflictFrameIds, conflictFrameId]
        : currentConflictFrameIds;
    this.patchSnapshot({
      operations: {
        ...this.snapshot.operations,
        [operation]: { pending: false, error: normalizedError },
      },
      saveConflictFrameIds,
    });
  }

  private clearSaveConflict(frameId: string): void {
    const current = this.snapshot.saveConflictFrameIds ?? [];
    if (!current.includes(frameId)) return;
    this.patchSnapshot({
      saveConflictFrameIds: current.filter((candidate) => candidate !== frameId),
    });
  }

  private isCurrentOperation(operation: AnnotationOperation, serial: number): boolean {
    return this.operationSerial.get(operation) === serial;
  }

  private setOperation(
    operation: AnnotationOperation,
    state: AnnotationStudioControllerSnapshot["operations"][AnnotationOperation],
  ): void {
    const current = this.snapshot.operations[operation];
    // 무변화 idle→idle 패치가 고빈도 경로(리소스 스왑마다)에서 전체 구독자를 깨우지 않게 한다.
    if (current.pending === state.pending && current.error === state.error) return;
    this.patchSnapshot({ operations: { ...this.snapshot.operations, [operation]: state } });
  }

  private setTrackingFrameSource(
    descriptor: ReplayableRenderSourceDescriptor<TrackingFrameRenderResource>,
    sessionSignal: AbortSignal,
  ): void {
    const descriptorKey = `${descriptor.id}:${descriptor.revision}`;
    if (this.trackingFrameResource?.descriptorKey === descriptorKey) {
      this.abortPendingTrackingFrameResource();
      return;
    }
    if (this.pendingTrackingFrameResource?.descriptorKey === descriptorKey) return;

    // acquire-후-release: 새 프레임이 준비될 때까지 현재 프레임을 유지한다. 먼저 해제하면
    // 스왑마다 null 구간(추가 notify + viewport fallback 점프)이 생기고 자원도 재획득된다.
    this.abortPendingTrackingFrameResource();
    if (sessionSignal.aborted || this.connections === 0 || this.disposed) return;

    const controller = new AbortController();
    const token = Symbol(descriptorKey);
    const abortAcquire = () => controller.abort();
    const detachSessionAbort = () => sessionSignal.removeEventListener("abort", abortAcquire);
    sessionSignal.addEventListener("abort", abortAcquire, { once: true });
    this.pendingTrackingFrameResource = {
      descriptorKey,
      token,
      controller,
      detachSessionAbort,
    };

    void Promise.resolve()
      .then(() => descriptor.acquire({ signal: controller.signal }))
      .then((resource) => {
        if (
          controller.signal.aborted ||
          sessionSignal.aborted ||
          this.connections === 0 ||
          this.disposed ||
          this.pendingTrackingFrameResource?.token !== token
        ) {
          resource.dispose();
          return;
        }
        const previous = this.trackingFrameResource;
        this.pendingTrackingFrameResource = null;
        this.trackingFrameResource = { descriptorKey, resource };
        previous?.resource.dispose();
        this.setOperation("render", idleOperation());
        this.notifyTrackingFrameResourceListeners();
      })
      .catch((error: unknown) => {
        if (
          controller.signal.aborted ||
          sessionSignal.aborted ||
          this.pendingTrackingFrameResource?.token !== token
        ) {
          return;
        }
        this.pendingTrackingFrameResource = null;
        const previous = this.trackingFrameResource;
        this.trackingFrameResource = null;
        previous?.resource.dispose();
        this.setOperation("render", {
          pending: false,
          error: normalizeOperationError("render", error),
        });
        this.notifyTrackingFrameResourceListeners();
      })
      .finally(() => {
        detachSessionAbort();
      });
  }

  private clearTrackingPreviewSession(): void {
    this.releaseTrackingPause();
    this.trackingSessionAbort?.abort();
    this.trackingSessionAbort = null;
    this.latestTrackingFrameDescriptor = null;
    this.releaseTrackingFrameResource();
  }

  private retainLatestTrackingFrameDescriptor(
    descriptor?: ReplayableRenderSourceDescriptor<TrackingFrameRenderResource>,
  ): void {
    const nextDescriptorKey = descriptor ? descriptorRegistryKey(descriptor) : undefined;
    if (
      this.pendingTrackingFrameResource &&
      this.pendingTrackingFrameResource.descriptorKey !== nextDescriptorKey
    ) {
      this.abortPendingTrackingFrameResource();
    }
    this.latestTrackingFrameDescriptor =
      descriptor && nextDescriptorKey ? { descriptorKey: nextDescriptorKey, descriptor } : null;
    if (!descriptor) {
      // A source-less producer frame must not leave the previous JPEG reachable or visible.
      this.releaseTrackingFrameResource();
    }
  }

  private abortPendingTrackingFrameResource(): void {
    const pending = this.pendingTrackingFrameResource;
    if (!pending) return;
    pending.detachSessionAbort();
    pending.controller.abort();
    this.pendingTrackingFrameResource = null;
  }

  private releaseTrackingFrameResource(): void {
    const changed =
      this.trackingFrameResource !== null || this.pendingTrackingFrameResource !== null;
    this.abortPendingTrackingFrameResource();
    this.trackingFrameResource?.resource.dispose();
    this.trackingFrameResource = null;
    if (changed) this.notifyTrackingFrameResourceListeners();
  }

  private notifyTrackingFrameResourceListeners(): void {
    for (const listener of this.trackingFrameResourceListeners) listener();
  }

  private notifyCapturedFrameResourceListeners(): void {
    for (const listener of this.capturedFrameResourceListeners) listener();
  }

  private async restoreActiveResources(): Promise<void> {
    if (this.connections === 0 || this.disposed || !this.connectionAbort) {
      this.disposeActiveResources();
      return;
    }
    const desired = this.desiredDescriptors();
    await Promise.all(
      (["frame", "mask"] as const).map((slot) =>
        this.ensureActiveResource(slot, desired.get(slot)),
      ),
    );
  }

  private async ensureActiveResource(
    slot: ResourceSlot,
    descriptor: ReplayableRenderSourceDescriptor | undefined,
  ): Promise<void> {
    const descriptorKey = descriptor ? `${descriptor.id}:${descriptor.revision}` : null;
    const current = this.activeResources.get(slot);
    if (current?.descriptorKey === descriptorKey) return;
    const pending = this.pendingResources.get(slot);
    if (pending?.descriptorKey === descriptorKey) {
      await pending.promise;
      return;
    }

    if (!descriptor || !this.connectionAbort) {
      current?.resource.dispose();
      this.activeResources.delete(slot);
      if (slot === "frame" && current) this.notifyCapturedFrameResourceListeners();
      return;
    }
    // acquire-후-release: 교체 리소스가 준비될 때까지 현재 리소스를 유지해 스왑 중간의
    // null notify(추가 리렌더 + viewport fallback)를 없앤다. 이전 리소스는 승자가 dispose.

    const activeDescriptorKey = `${descriptor.id}:${descriptor.revision}`;
    const connectionSignal = this.connectionAbort.signal;
    const token = Symbol(activeDescriptorKey);
    const promise = (async () => {
      try {
        const resource = await descriptor.acquire({ signal: connectionSignal });
        const desiredNow = this.desiredDescriptors().get(slot);
        const desiredKey = desiredNow ? `${desiredNow.id}:${desiredNow.revision}` : null;
        if (
          connectionSignal.aborted ||
          this.connections === 0 ||
          desiredKey !== activeDescriptorKey
        ) {
          resource.dispose();
          return;
        }
        const previous = this.activeResources.get(slot);
        this.activeResources.set(slot, { descriptorKey: activeDescriptorKey, resource });
        previous?.resource.dispose();
        if (slot === "frame") this.notifyCapturedFrameResourceListeners();
        this.setOperation("render", idleOperation());
      } catch (error) {
        if (connectionSignal.aborted) return;
        this.setOperation("render", {
          pending: false,
          error: normalizeOperationError("render", error),
        });
      } finally {
        const latest = this.pendingResources.get(slot);
        if (latest?.token === token) this.pendingResources.delete(slot);
      }
    })();
    this.pendingResources.set(slot, { descriptorKey: activeDescriptorKey, token, promise });
    await promise;
  }

  private desiredDescriptors(): Map<ResourceSlot, ReplayableRenderSourceDescriptor> {
    const desired = new Map<ResourceSlot, ReplayableRenderSourceDescriptor>();
    if (this.snapshot.viewMode !== "annotate") return desired;
    const context = this.activeTargetContext();
    if (!context) return desired;
    const frameDescriptor = this.descriptors.get(descriptorRegistryKey(context.frame.frameSource));
    if (frameDescriptor) desired.set("frame", frameDescriptor);
    const maskRef =
      context.target.contours === undefined ? context.target.maskSource?.id : undefined;
    if (maskRef && context.target.maskSource) {
      const maskDescriptor = this.descriptors.get(descriptorRegistryKey(context.target.maskSource));
      if (maskDescriptor) desired.set("mask", maskDescriptor);
    }
    return desired;
  }

  private disposeActiveResources(): void {
    const hadFrameResource = this.activeResources.has("frame");
    for (const active of this.activeResources.values()) active.resource.dispose();
    this.activeResources.clear();
    if (hadFrameResource) this.notifyCapturedFrameResourceListeners();
  }

  // driver getAvailability는 context(workspace/selection/draft)의 순수 함수라는 계약이므로
  // 같은 identity 조합이면 재호출하지 않는다. availability 객체 identity가 유지되어야
  // annotationSetValidation 스냅샷 identity도 무변화 패치에서 안정된다.
  private resolveAvailability(snapshot: AnnotationStudioControllerSnapshot): {
    authoring?: AnnotationOperationAvailability;
    segmentation?: AnnotationOperationAvailability;
    tracking?: AnnotationOperationAvailability;
    validation?: AnnotationOperationAvailability;
  } {
    const cache = this.availabilityCache;
    if (
      cache &&
      cache.workspace === snapshot.workspace &&
      cache.sequenceId === snapshot.selectedSequenceId &&
      cache.streamId === snapshot.selectedStreamId &&
      cache.targetId === snapshot.selectedTargetId &&
      cache.draft === snapshot.draft
    ) {
      return cache;
    }
    const context = {
      workspace: snapshot.workspace,
      sequenceId: snapshot.selectedSequenceId,
      streamId: snapshot.selectedStreamId,
      targetId: snapshot.selectedTargetId,
      draft: snapshot.draft,
    };
    const next = {
      workspace: snapshot.workspace,
      sequenceId: snapshot.selectedSequenceId,
      streamId: snapshot.selectedStreamId,
      targetId: snapshot.selectedTargetId,
      draft: snapshot.draft,
      authoring:
        this.options.mode === "authoring"
          ? this.options.authoring.getAvailability(context)
          : undefined,
      segmentation: this.options.segmentation?.getAvailability(context),
      tracking: this.options.trackingPreview?.getAvailability(context),
      validation: this.options.annotationSetValidation?.getAvailability(context),
    };
    this.availabilityCache = next;
    return next;
  }

  private patchSnapshot(patch: Partial<AnnotationStudioControllerSnapshot>): void {
    let nextSnapshot = { ...this.snapshot, ...patch };
    const dirtyFrameIds = new Set(
      nextSnapshot.draft?.dirtyScopes.flatMap((scope) =>
        scope.kind === "target" ? [scope.frameId] : [],
      ) ?? [],
    );
    const saveConflictFrameIds = (nextSnapshot.saveConflictFrameIds ?? []).filter(
      (frameId) => dirtyFrameIds.has(frameId) && nextSnapshot.draft?.frames[frameId] !== undefined,
    );
    if (
      saveConflictFrameIds.length !== (nextSnapshot.saveConflictFrameIds?.length ?? 0) ||
      nextSnapshot.saveConflictFrameIds === undefined
    ) {
      nextSnapshot = { ...nextSnapshot, saveConflictFrameIds };
    }
    const resolvedAvailability = this.resolveAvailability(nextSnapshot);
    if (this.options.mode === "authoring") {
      nextSnapshot = {
        ...nextSnapshot,
        authoringAvailability: resolvedAvailability.authoring,
      };
    }
    if (this.options.segmentation) {
      nextSnapshot = {
        ...nextSnapshot,
        segmentationAvailability: resolvedAvailability.segmentation,
      };
    } else if (this.options.mode === "authoring") {
      nextSnapshot = {
        ...nextSnapshot,
        segmentationAvailability: nextSnapshot.authoringAvailability,
      };
    }
    if (this.options.trackingPreview) {
      nextSnapshot = {
        ...nextSnapshot,
        trackingAvailability: resolvedAvailability.tracking,
      };
    }
    const validationDriver = this.options.annotationSetValidation;
    const validationState = nextSnapshot.annotationSetValidation;
    if (validationDriver && validationState && resolvedAvailability.validation) {
      const availability = resolvedAvailability.validation;
      // The controller owns immutable draft updates, so the exact fields sent to validation are
      // sufficient invalidation keys. activeFrameId/dirtyScopes changes intentionally do not
      // invalidate a result and no full-draft serialization is needed on interaction paths.
      const canonicalRevisionChanged =
        validationDriver.validationSource === "canonical" &&
        validationState.sourceRevision !== undefined &&
        !Object.is(
          validationState.sourceRevision,
          validationDriver.getSourceRevision?.({
            workspace: nextSnapshot.workspace,
            sequenceId: nextSnapshot.selectedSequenceId,
            streamId: nextSnapshot.selectedStreamId,
            targetId: nextSnapshot.selectedTargetId,
            draft: nextSnapshot.draft,
          }),
        );
      const contentChanged =
        canonicalRevisionChanged ||
        !annotationSetValidationInputsMatch(
          annotationSetValidationInputIdentity(
            this.snapshot,
            validationDriver.validationSource === "canonical",
          ),
          annotationSetValidationInputIdentity(
            nextSnapshot,
            validationDriver.validationSource === "canonical",
          ),
        );
      if (contentChanged && validationState.status !== "idle") {
        this.annotationSetValidationController?.abort();
        this.annotationSetValidationController = null;
        this.annotationSetValidationSerial += 1;
        const hasDraftFrames =
          (nextSnapshot.draft !== null && nextSnapshot.draft.orderedFrameIds.length > 0) ||
          (validationDriver.validationSource === "canonical" &&
            (nextSnapshot.savedFrameIndex?.orderedFrames.length ?? 0) > 0);
        const unresolvedIssues =
          validationState.status === "blocked" || validationState.status === "stale"
            ? validationState.issues
            : [];
        nextSnapshot = {
          ...nextSnapshot,
          annotationSetValidation: {
            status: hasDraftFrames ? "stale" : "idle",
            availability,
            issues: hasDraftFrames ? unresolvedIssues : [],
            error: null,
            ...(validationState.sourceRevision !== undefined
              ? { sourceRevision: validationState.sourceRevision }
              : {}),
          },
        };
      } else if (validationState.availability !== availability) {
        nextSnapshot = {
          ...nextSnapshot,
          annotationSetValidation: { ...validationState, availability },
        };
      }
    }
    this.snapshot = nextSnapshot;
    for (const listener of this.listeners) listener();
  }

  private assertAuthoring(operation: AnnotationOperation): void {
    this.assertNotDisposed();
    if (this.options.mode !== "authoring") {
      throw new AnnotationOperationError(operation, "review-only");
    }
  }

  private getAuthoringAvailability(): AnnotationOperationAvailability {
    if (this.options.mode !== "authoring") return { allowed: false };
    return this.options.authoring.getAvailability(this.operationContext());
  }

  private assertNotDisposed(): void {
    if (this.disposed) throw new Error("AnnotationStudioController has been disposed.");
  }
}

export function createAnnotationStudioController(
  options: AnnotationStudioControllerOptions,
): AnnotationStudioController {
  return new DefaultAnnotationStudioController(options);
}
